<script setup lang="ts">
import { computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, shallowReactive, shallowRef, watch } from "vue";
import type { ComponentPublicInstance } from "vue";
import { useDebounceFn, useMediaQuery } from "@vueuse/core";
import { FocusScope } from "reka-ui";
import { useMessage } from "zenless-ui";
import EmblaCarousel from "embla-carousel";
import type { EmblaCarouselType } from "embla-carousel";
import type { Author, Comment, Post } from "~/types/entities";
import type { PostPreview } from "~/composables/usePostModal";
import { resolveErrorMessage } from "~/utils/api-error";
import { useRenderedBody } from "~/composables/useRenderedBody";
import { openPostLink } from "~/utils/post-link";
import { formatTime } from "~/utils/time";
import { StarIcon, AtSymbolIcon, EyeIcon, EyeSlashIcon, PhotoIcon, EllipsisVerticalIcon, FaceSmileIcon } from "@heroicons/vue/24/outline";
import { HeartIcon, StarIcon as StarIconSolid } from "@heroicons/vue/24/solid";
import { useMentionInput } from "~/composables/useMentionInput";
import { useScrollHint } from "~/composables/useScrollHint";
import type { MentionCandidate, MentionAnchor, MentionRange, DisplaySegment } from "~/composables/useMentionInput";
import { useEmoteInsert } from "~/composables/useEmoteInsert";
import type { EmoteRange } from "~/composables/useEmoteInsert";
import { isAnyGalleryOpen } from "~/composables/useImageGallery";
import { useCommentSeek } from "~/composables/useCommentSeek";
import { useCommentReadHistory, findTopVisibleComment } from "~/composables/useCommentReadHistory";
import { countNewReply, prependKeepingPosition, refreshKeepingPosition, removeFromTimeline } from "~/utils/comment-timeline";
import { toCardCoverThumbUrl, toThumbUrl, toCanonicalUrl, toNoResizeWebpUrl } from "~/utils/image";

// 静态导入子组件以避免运行时链式异步解析带来的视觉卡顿和加载迟滞
import BilibiliPlayer from "./BilibiliPlayer.vue";
import UserHoverCard from "./UserHoverCard.vue";
import IkZzzMarquee from "./IkZzzMarquee.vue";
import CommentItem from "./CommentItem.vue";
import ScrollHintArrows from "./ScrollHintArrows.vue";
import CoverPager from "./CoverPager.vue";
import CommentsToolbar from "./CommentsToolbar.vue";
import type { CommentSort } from "~/composables/useApi";
import MentionHighlightOverlay from "./MentionHighlightOverlay.vue";
import MentionPicker from "./MentionPicker.vue";
import EmotePicker from "./EmotePicker.vue";
import MobileEmotePicker from "./MobileEmotePicker.vue";

const DEFAULT_COVER_IMAGE = "/images/default-cover.webp";

const { isLoading: isGalleryLoading, loadingProgress: galleryProgress, openGallery, preload: preloadGallery, destroy: destroyPreview } = useImageGallery();

const props = defineProps<{
  postId: string;
  entryKey: string;
  coverHint?: number | null;
  preview?: PostPreview | null;
  targetCommentId?: string | null;
}>();

const emit = defineEmits<{
  close: [];
}>();

const api = useApi();
const auth = useAuthStore();
const postModal = usePostModal();
const loginDialog = useLoginDialog();
const confirmDialog = useConfirmDialog();
const reportDialog = useReportDialog();
const message = useMessage();
const initialWidth = typeof window !== "undefined" ? window.innerWidth : 0;
const isMobile = useMediaQuery("(max-width: 768px)", { ssrWidth: initialWidth });
const isCompact = useMediaQuery("(max-width: 1024px)", { ssrWidth: initialWidth });

const post = ref<Post | null>(null);
const loading = ref(true);
const loadError = ref(false);
const isActiveEntry = computed(() => postModal.isOpen.value && postModal.entryKey.value === props.entryKey);

// 正文渲染（markdown-it + DOMPurify）按需异步加载，不进首屏 chunk。
const { bodyHtml, hasContent: bodyHasContent } = useRenderedBody(post);

// 弹窗刚打开时优先展示首页卡片传入的 preview，等接口数据回来后无缝替换。
const headerAuthor = computed<Author | null>(() => post.value?.author ?? props.preview?.author ?? null);
const headerCreatedAt = computed<string | undefined>(() =>
  post.value?.firstPublishedAt || post.value?.publishedAt || post.value?.createdAt
  || props.preview?.firstPublishedAt || props.preview?.publishedAt || props.preview?.createdAt
);
const headerTitle = computed<string>(() => post.value?.title ?? props.preview?.title ?? "");
const headerCategory = computed(() => post.value?.category ?? props.preview?.category ?? null);

// 评论时间线：按 commentSort 排（每次进帖子都从最早开始）。featured 是放在时间线开头的采纳 / 置顶（时间线里没有它们，只在加载到开头时才有）。
const commentSort = ref<CommentSort>("oldest");
const comments = ref<Comment[]>([]);
const featuredComments = ref<Comment[]>([]);
const commentsCursor = ref("");
const commentsHasNext = ref(true);
// 从中间某条评论开始加载时，往回（列表上方）翻页的游标。
const commentsPrevCursor = ref("");
const commentsLoadingEarlier = ref(false);
const commentsLoading = ref(false);
// 首次评论加载是否已结束。评论加载被刻意延后（scheduleLoadComments 用
// rAF + requestIdleCallback），在它真正开始前 commentsLoading 仍是 false，
// 若此时就按「无评论」显示空提示，会出现「空提示 → 骨架 → 评论」的闪烁。
// 用此标记把「尚未加载」与「加载完确实为空」区分开：未加载完一律显示骨架。
const commentsLoaded = ref(false);
// 正文上屏后再渲染评论列表，避免和正文挂载挤在同一帧里挂载大量 CommentItem 与 useEmotes 触发 fetch。
const commentsVisible = ref(false);
// 评论列表上屏后再给滚动容器加上 will-change/translateZ(0) 提升层，
// 既保留开场阶段少合成层的好处，又让后续上下滚动能复用独立层。
const isScrollable = ref(false);
// 弹窗内跑马灯同样等正文、评论都上屏后再启动，避免与它们争抢同一帧。
const marqueeReady = ref(false);

const showCommentsSkeleton = computed(() => {
  if (!commentsVisible.value && comments.value.length > 0) return true;
  return comments.value.length === 0 && (!commentsLoaded.value || commentsLoading.value);
});
const showCommentsEmpty = computed(
  () => comments.value.length === 0 && featuredComments.value.length === 0 && commentsLoaded.value && !commentsLoading.value,
);

// 用于在 postId 切换时废弃旧的评论请求，避免旧数据污染新委托
let currentCommentLoadId = 0;
// 组件卸载时阻止继续加载评论
let isCommentLoadingCancelled = false;

const newComment = ref("");
const sendingComment = ref(false);
const commentInputBoxRef = ref<HTMLElement | null>(null);
const commentInputFocused = ref(false);
const replyTarget = ref<{ id: string; authorName: string } | null>(null);
const commentAnonymous = ref(false);

/** 真实底层 textarea：z-input 是个包装，原生 keydown / overlay 都需要它 */
const commentTextareaRef = ref<HTMLTextAreaElement | null>(null);

// 评论输入框首次 focus 前不需要 @ 提及/表情插入逻辑，延迟初始化以减少弹窗打开瞬间 JS 执行。
const mentionInputInitialized = ref(false);

type MentionInputAPI = ReturnType<typeof useMentionInput>;
type EmoteInsertAPI = ReturnType<typeof useEmoteInsert>;

const mention: MentionInputAPI = shallowReactive<MentionInputAPI>({
  pickerVisible: ref(false),
  pickerLoading: ref(false),
  pickerResults: ref<MentionCandidate[]>([]),
  pickerActiveIndex: ref(0),
  pickerAnchor: ref<MentionAnchor | null>(null),
  refresh: () => {},
  onKeyDown: () => {},
  selectCandidate: () => {},
  insertAtTrigger: () => {
    ensureMentionInputInitialized();
    void mention.insertAtTrigger();
  },
  prependMentionChip: () => {},
  serializeForSend: () => newComment.value,
  hydrateFromContent: () => {},
  reset: () => {},
  mentions: ref<MentionRange[]>([]),
  overlaySegments: computed<DisplaySegment[]>(() => []),
  isAtLimit: computed(() => false),
});

const emoteInsert: EmoteInsertAPI = shallowReactive<EmoteInsertAPI>({
  emotes: ref<EmoteRange[]>([]),
  insertEmote: async (code: string, options?: { focus?: boolean }) => {
    ensureMentionInputInitialized();
    return emoteInsert.insertEmote(code, options);
  },
  insertText: async (text: string, options?: { focus?: boolean }) => {
    ensureMentionInputInitialized();
    return emoteInsert.insertText(text, options);
  },
  refresh: () => {},
  onKeyDown: () => {},
  serializeWith: () => newComment.value,
  reset: () => {},
  isAtLimit: computed(() => false),
  hasEmotes: computed(() => false),
});

const emotePickerVisible = ref(false);
const emotePickerAnchor = ref<{ top: number; left: number; height: number } | null>(null);

/** 先等两帧让上一步的渲染上屏，再挑主线程空闲时执行；返回取消函数 */
function afterPaintIdle(run: () => void, timeout = 300): () => void {
  let cancelled = false;
  let idleId: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const fire = () => {
    if (!cancelled) run();
  };
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (cancelled) return;
    if (typeof requestIdleCallback !== "undefined") idleId = requestIdleCallback(fire, { timeout });
    else timer = setTimeout(fire, 16);
  }));
  return () => {
    cancelled = true;
    if (idleId !== null) cancelIdleCallback(idleId);
    if (timer) clearTimeout(timer);
  };
}

let cancelRevealWork: (() => void) | null = null;
let revealDelayTimer: ReturnType<typeof setTimeout> | null = null;

function cancelScheduledRevealWork() {
  cancelRevealWork?.();
  cancelRevealWork = null;
  if (revealDelayTimer) {
    clearTimeout(revealDelayTimer);
    revealDelayTimer = null;
  }
}

// 正文上屏后按「评论列表 → 滚动层提升 + 跑马灯」分帧推进，每步都等上一步上屏、
// 主线程空闲后再做，避免几件重活挤在动画刚停下的同一帧里。
function scheduleRevealWork() {
  cancelScheduledRevealWork();
  commentsVisible.value = false;
  const start = () => {
    revealDelayTimer = null;
    cancelRevealWork = afterPaintIdle(() => {
      commentsVisible.value = true;
      cancelRevealWork = afterPaintIdle(() => {
        cancelRevealWork = null;
        isScrollable.value = true;
        marqueeReady.value = true;
      });
    });
  };
  // 浮层内切换委托时，等弹窗主体的重放动画播完再开始
  const switchRemaining = switchAnimationEndsAt - performance.now();
  if (switchRemaining > 0) revealDelayTimer = setTimeout(start, switchRemaining);
  else start();
}

const toggleEmotePicker = (e: MouseEvent) => {
  if (emotePickerVisible.value) {
    emotePickerVisible.value = false;
    focusCommentInput();
    return;
  }
  const target = e.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  emotePickerAnchor.value = { top: rect.top, left: rect.left, height: rect.height };
  emotePickerVisible.value = true;
  if (isMobile.value) {
    const ta = commentInputBoxRef.value?.querySelector("textarea") as HTMLTextAreaElement | null;
    ta?.blur();
  }
};

const onEmoteSelect = async (emote: { code: string }) => {
  const ok = await emoteInsert.insertEmote(emote.code, { focus: !isMobile.value });
  if (!ok) {
    message.warning("表情数量已达上限");
  }
  if (!isMobile.value) {
    emotePickerVisible.value = false;
  }
};

const onEmojiSelect = async (emoji: string) => {
  await emoteInsert.insertText(emoji, { focus: !isMobile.value });
  if (!isMobile.value) {
    emotePickerVisible.value = false;
  }
};

const commentImages = useCommentImages();

const scrollRef = ref<HTMLElement | null>(null);
const commentsScrollRef = ref<HTMLElement | null>(null);
/** 桌面端正文单独一列滚动（封面固定在左侧）；移动端跟着整页滚，不用它。 */
const detailScrollRef = ref<HTMLElement | null>(null);
const detailHint = useScrollHint(computed(() => isActiveEntry.value ? detailScrollRef.value : null));
const covers = computed(() => post.value?.covers ?? []);
const hasCovers = computed(() => covers.value.length > 0);
const isCommentEditorActive = computed(
  () =>
    commentInputFocused.value ||
    commentImages.uploadTasks.value.length > 0 ||
    !!newComment.value.trim() ||
    emoteInsert.hasEmotes.value,
);
const hasCommentContent = computed(
  () =>
    !!newComment.value.trim() ||
    emoteInsert.hasEmotes.value ||
    commentImages.uploadedImageIds.value.length > 0,
);
const canSubmitComment = computed(
  () =>
    !sendingComment.value &&
    !commentImages.hasPendingUploads.value &&
    !commentImages.hasErroredUploads.value &&
    hasCommentContent.value,
);
const postLikeCount = computed(() => post.value?.likesCount ?? 0);
const postCommentCount = computed(() => post.value?.commentsCount ?? comments.value.length);

const syncCommentInputHeight = async () => {
  await nextTick();
  const textarea = commentInputBoxRef.value?.querySelector("textarea") as HTMLTextAreaElement | null;
  if (!textarea) return;
  textarea.style.height = "42px";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 62)}px`;
};

const firstCover = computed(() => covers.value[0] ?? null);
const previewCover = computed(() => {
  const cover = props.preview?.cover?.trim();
  // 预览图仅作为 blur-up 占位，统一走缩略图，避免首页卡片传入大原图
  // 时 decode() 占用过多资源导致弹窗入场掉帧。与卡片封面同一 URL，入场时直接命中缓存。
  return cover ? toCardCoverThumbUrl(cover) : null;
});
const coverPreviewSrc = (i: number) => {
  const cover = covers.value[i];
  if (!cover) return undefined;
  return (i === 0 && previewCover.value) || toThumbUrl(cover.url, 360) || undefined;
};

// 弹窗封面直接显示原图，与放大预览保持一致；
// 入场占位图仍用缩略图做 blur-up，避免首屏 decode 大图掉帧。
const coverDisplaySrc = (url: string | undefined) => {
  if (!url) return url;
  return toCanonicalUrl(url);
};

// 多图轮播不能直接放原图：几千像素的原图每次滑入视口都要在光栅线程整张解码
// （实测 2700×4800 单张 300ms+），合成器等解码完成才提交，切图动画因此严重掉帧。
// 轮播里用与显示尺寸匹配的缩略图，点开放大预览时仍走原图。
const SLIDE_DISPLAY_WIDTH_MOBILE = 800;
const SLIDE_DISPLAY_WIDTH = 1200;
const slideDisplaySrc = (cover: { url: string; width?: number | null }) => {
  const maxWidth = isMobile.value ? SLIDE_DISPLAY_WIDTH_MOBILE : SLIDE_DISPLAY_WIDTH;
  // 原图本身不超过目标宽度时只转 WebP，避免被 CDN 放大变糊
  if (cover.width && cover.width > 0 && cover.width <= maxWidth) return toNoResizeWebpUrl(cover.url);
  return toThumbUrl(cover.url, maxWidth);
};
const loadedPreviewImageRef = ref<HTMLImageElement | null>(null);
const setLoadedPreviewImage = (el: Element | ComponentPublicInstance | null) => {
  loadedPreviewImageRef.value = el instanceof HTMLImageElement ? el : null;
};

// 真实图片解码完成的索引集合：用于在解码完成前继续显示骨架屏，
// 避免"骨架结束 → 黑色封面框 → 图片淡入"的中间黑屏。
const loadedCoverImages = ref<Set<number>>(new Set());
const isCoverImageLoaded = (i: number) => loadedCoverImages.value.has(i);
const onCoverImageLoad = (i: number) => {
  if (!loadedCoverImages.value.has(i)) {
    loadedCoverImages.value = new Set([...loadedCoverImages.value, i]);
  }
};
const coverAspectRatio = computed(() => {
  const c = firstCover.value;
  if (c?.width && c?.height && c.width > 0 && c.height > 0) return c.width / c.height;
  // 无图片封面但有 B 站视频时，按 16:9 展示播放器。
  if (post.value?.externalVideos?.length && !hasCovers.value) return 16 / 9;
  // 无真实封面时优先沿用骨架阶段使用的 coverHint，避免骨架→默认占位图之间的高度跳动。
  if (props.coverHint && props.coverHint > 0) return props.coverHint;
  return 643 / 408;
});

/** 第 i 张封面在页面上的 <img>（轮播只渲染当前附近的几张，所以关闭时再取）。 */
const coverImageEl = (i: number): HTMLElement | null => {
  if (covers.value.length === 1) return dialogBodyRef.value?.querySelector<HTMLElement>(".ik-dialog__cover") ?? null;
  const slide = emblaRef.value?.querySelectorAll<HTMLElement>(".ik-dialog__cover-slide")[i];
  return slide?.querySelector<HTMLElement>(".ik-dialog__cover") ?? null;
};

const openCoverPreview = (index = 0) => {
  const images = covers.value.map((c, i) => ({
    src: toCanonicalUrl(c.url),
    thumb: toThumbUrl(c.url),
    width: c.width,
    height: c.height,
    element: () => coverImageEl(i),
  }));
  if (!images.length) return;
  openGallery(images, Math.min(Math.max(index, 0), images.length - 1), {
    // 画廊里翻页时轮播跟着跳过去，关闭时才能缩回对应的那张
    onChange: (i: number) => emblaApi.value?.scrollTo(i, true),
  });
};

/* ── 封面轮播 ─────────────────────────────────── */
const coverIndex = ref(0);
const emblaRef = shallowRef<HTMLElement | null>(null);
const emblaApi = shallowRef<EmblaCarouselType | undefined>();

// 手动管理 Embla 生命周期：v-else 里轮播容器在 covers 拿到后才渲染，
// 直接用 useEmblaCarousel 的 onMounted 会导致实例始终无法创建。
const syncEmblaState = () => {
  const api = emblaApi.value;
  if (!api) return;
  coverIndex.value = api.selectedScrollSnap();
  expandLoadWindow();
};

const destroyEmbla = () => {
  const api = emblaApi.value;
  if (!api) return;
  // destroy() 会清空轨道的 transform。组件卸载时作用域先停（触发这里），
  // DOM 却要等 200ms 离场动画播完才移除，不保留位移的话离场期间会瞬间跳回第一张。
  const container = api.containerNode();
  const transform = container.style.transform;
  api.destroy();
  if (transform) container.style.transform = transform;
  emblaApi.value = undefined;
};

const initEmbla = (el: HTMLElement) => {
  destroyEmbla();
  emblaApi.value = EmblaCarousel(el, {
    loop: false,
    align: "start",
    dragThreshold: 6,
    startIndex: coverIndex.value,
  });
  emblaApi.value.on("select", syncEmblaState);
  emblaApi.value.on("reInit", syncEmblaState);
  syncEmblaState();
};

watch(emblaRef, (el, _, onCleanup) => {
  if (el && isActiveEntry.value) initEmbla(el);
  onCleanup(() => destroyEmbla());
}, { flush: "post" });

// 已批准加载的封面索引集合：只有命中其中的图片才会真正请求 src。
// 设计目的：让新图的网络请求 + 解码不要砸在切换动画的同一帧里。
// 切换动画由 Embla 在合成器线程驱动，但相邻图仍按需加载，避免一次性拉取全部。
const loadedCoverIndices = ref<Set<number>>(new Set([0, 1, 2]));
const LOAD_WINDOW_RADIUS = 2;

const expandLoadWindow = () => {
  const i = coverIndex.value;
  const total = covers.value.length;
  if (total === 0) return;
  const next = new Set(loadedCoverIndices.value);
  let changed = false;
  for (let k = i - LOAD_WINDOW_RADIUS; k <= i + LOAD_WINDOW_RADIUS; k++) {
    if (k >= 0 && k < total && !next.has(k)) {
      next.add(k);
      changed = true;
    }
  }
  if (changed) loadedCoverIndices.value = next;
};

const resetLoadWindow = () => {
  // 重置到初始窗口（前三张），与首次进入时一致
  loadedCoverIndices.value = new Set([0, 1, 2]);
};

// 命中加载窗口、或正好是当前焦点的图片，才允许真正请求 src。
const isCoverNearby = (i: number) =>
  i === coverIndex.value || loadedCoverIndices.value.has(i);

const goCover = (index: number) => {
  const api = emblaApi.value;
  if (!api) return;
  const total = covers.value.length;
  if (total <= 1) return;
  const target = Math.min(Math.max(index, 0), total - 1);
  api.scrollTo(target);
};

// 拦截滚轮：只拦截会被横向滑动吞掉的那部分（平板/鼠标水平滚轮），
// 转换为外层弹窗的垂直滚动，避免被误判为切图。
// 垂直滚轮（deltaY）不拦截——交给浏览器原生平滑滚动处理，避免"瞬移"感。
const onCoverWheel = (e: WheelEvent) => {
  if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
  e.preventDefault();
  const parent = scrollRef.value;
  if (parent) parent.scrollTop += e.deltaX;
};

// 切换委托时重置封面索引并将轮播滚回起点
watch(() => props.postId, () => {
  coverIndex.value = 0;
  resetLoadWindow();
  loadedCoverImages.value = new Set();
  nextTick(() => {
    emblaApi.value?.scrollTo(0, true);
  });
});

// 封面列表变化后重新初始化 Embla，并立即扩张当前索引的加载窗口
watch(covers, () => {
  nextTick(() => {
    if (!isActiveEntry.value) return;
    emblaApi.value?.reInit();
    expandLoadWindow();
  });
});

/* ── 数据加载 ──────────────────────────────────── */
let currentPostLoadId = 0;
let isUnmounted = false;
interface PostRequest {
  loadId: number;
  postId: string;
  generation: number;
}
const isCurrentPostRequest = (request: PostRequest) =>
  !isUnmounted && request.loadId === currentPostLoadId &&
  request.postId === props.postId && request.generation === auth.generation &&
  isActiveEntry.value && postModal.postId.value === request.postId;

const loadPost = async () => {
  const request: PostRequest = {
    loadId: ++currentPostLoadId,
    postId: props.postId,
    generation: auth.generation,
  };
  try {
    const loadedPost = await api.getPost(request.postId);
    // 正文整块挂载开销大，等入场动画结束再渲染；入场期间由骨架屏 + preview 占位。
    await postModal.whenEntered();
    if (!isCurrentPostRequest(request)) return null;
    post.value = loadedPost;
    if (post.value?.title) {
      postModal.setTitle(post.value.title);
    }
  } catch (err) {
    if (!isCurrentPostRequest(request)) return null;
    loadError.value = true;
    message.error(resolveErrorMessage(err, "获取委托详情失败"));
  }
  return request;
};

const waitForLoadedPreview = async (request: PostRequest) => {
  await nextTick();
  if (!isCurrentPostRequest(request)) return false;
  if (!previewCover.value) return true;

  const image = loadedPreviewImageRef.value;
  if (image) {
    try {
      // 移动端/平板 GPU/CPU 同时处理弹窗入场动画与图片 decode 容易掉帧，
      // 且 decode 可能长时间不 resolve 导致骨架屏卡死。
      // 短超时后先解除骨架屏，让真实图片异步加载；blur-up 预览图已占位，避免黑屏。
      const timeoutMs = isCompact.value ? 200 : 1200;
      await Promise.race([
        image.decode(),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error("decode timeout")), timeoutMs)),
      ]);
    } catch {
      // 解码失败或超时：继续显示 preview/骨架，让 img@load 事件最终替换
    }
  }
  if (!isCurrentPostRequest(request)) return false;

  if (import.meta.client) {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }
  return isCurrentPostRequest(request);
};

const isCurrentCommentRequest = (request: PostRequest) =>
  !isCommentLoadingCancelled && request.loadId === currentCommentLoadId &&
  request.postId === props.postId && request.generation === auth.generation &&
  isActiveEntry.value && postModal.postId.value === request.postId;

const loadComments = async (isSeekCurrent = () => true) => {
  if (isCommentLoadingCancelled || !isActiveEntry.value || commentsLoading.value || !commentsHasNext.value) return;
  commentsLoading.value = true;
  const request = { loadId: ++currentCommentLoadId, postId: props.postId, generation: auth.generation };
  try {
    const page = await api.getComments(
      request.postId,
      commentsCursor.value,
      commentsCursor.value === "",
      { sort: commentSort.value },
    );
    if (!isCurrentCommentRequest(request) || !isSeekCurrent()) return;
    if (!commentsCursor.value) featuredComments.value = page.featured;
    comments.value.push(...page.nodes);
    commentsCursor.value = page.endCursor;
    commentsHasNext.value = page.hasNextPage;
  } catch (err) {
    if (!isCurrentCommentRequest(request) || !isSeekCurrent()) return;
    message.error(resolveErrorMessage(err, "获取评论失败"));
  } finally {
    if (isCurrentCommentRequest(request)) {
      commentsLoading.value = false;
      commentsLoaded.value = true;
    }
  }
};

/** 从某条评论所在的楼层重新加载时间线（通知跳转、点引用跳到还没加载的楼层）。 */
const loadCommentsAround = async (commentId: string, isSeekCurrent = () => true) => {
  if (isCommentLoadingCancelled || !isActiveEntry.value) return;
  const request = { loadId: ++currentCommentLoadId, postId: props.postId, generation: auth.generation };
  commentsLoading.value = true;
  try {
    const page = await api.getComments(request.postId, "", false, { anchor: commentId, sort: commentSort.value });
    if (!isCurrentCommentRequest(request) || !isSeekCurrent()) return;
    // 只有加载到时间线开头时后端才给采纳 / 置顶（锚点就是它们、或锚点不可见时也按第一页处理）。
    featuredComments.value = page.prevCursor ? [] : page.featured;
    comments.value = page.nodes;
    commentsCursor.value = page.endCursor;
    commentsHasNext.value = page.hasNextPage;
    commentsPrevCursor.value = page.prevCursor;
  } catch (err) {
    if (!isCurrentCommentRequest(request) || !isSeekCurrent()) return;
    message.error(resolveErrorMessage(err, "获取评论失败"));
  } finally {
    if (isCurrentCommentRequest(request)) {
      commentsLoading.value = false;
      commentsLoaded.value = true;
    }
  }
};

const loadEarlierComments = async () => {
  if (!isActiveEntry.value || !commentsPrevCursor.value || commentsLoadingEarlier.value) return;
  commentsLoadingEarlier.value = true;
  const request = { loadId: currentCommentLoadId, postId: props.postId, generation: auth.generation };
  try {
    const page = await api.getComments(request.postId, commentsPrevCursor.value, false, { sort: commentSort.value });
    if (!isCurrentCommentRequest(request)) return;
    await prependKeepingPosition(
      comments.value[0]?.id,
      () => {
        comments.value.unshift(...page.nodes);
        commentsPrevCursor.value = page.prevCursor;
        // 翻回开头了：采纳 / 置顶放回顶部。
        if (!page.prevCursor) featuredComments.value = page.featured;
      },
      getActiveCommentsContainer(),
    );
  } catch (err) {
    if (isCurrentCommentRequest(request)) message.error(resolveErrorMessage(err, "获取评论失败"));
  } finally {
    if (isCurrentCommentRequest(request)) commentsLoadingEarlier.value = false;
  }
};

const resetCommentTimeline = () => {
  comments.value = [];
  featuredComments.value = [];
  commentsCursor.value = "";
  commentsPrevCursor.value = "";
  commentsHasNext.value = true;
};

const refreshComments = async () => {
  commentsLoading.value = false;
  currentCommentLoadId++;
  resetCommentTimeline();
  commentsLoaded.value = false;
  await loadComments();
};

const targetCommentId = computed(() => props.targetCommentId ?? null);

const { seek, cancel: cancelSeek, pause: pauseSeek, resume: resumeSeek, highlightedCommentId } = useCommentSeek({
  targetCommentId,
  comments,
  featured: featuredComments,
  commentsHasNext,
  loadComments,
  commentsVisible,
  loadAround: loadCommentsAround,
  root: commentsScrollRef,
});

watch(
  [() => props.postId, () => auth.generation, isActiveEntry],
  ([id, generation, active], [previousId, previousGeneration, previousActive]) => {
    if (id !== previousId || generation !== previousGeneration) cancelSeek();
    else if (!active && previousActive) pauseSeek();
    currentCommentLoadId++;
    commentsLoading.value = false;
    commentsLoadingEarlier.value = false;
  },
  { flush: "sync" },
);

const jumpToComment = (commentId: string) => {
  void seek(commentId);
};

const changeCommentSort = async (sort: CommentSort) => {
  if (sort === commentSort.value) return;
  commentSort.value = sort;
  if (isCompact.value) {
    await refreshKeepingPosition(dialogBodyRef.value, commentsInnerRef.value, refreshComments);
  } else {
    commentsScrollRef.value?.scrollTo({ top: 0 });
    await refreshComments();
  }
};

const commentHistory = useCommentReadHistory();
const commentsInnerRef = ref<HTMLElement | null>(null);
const commentsHint = useScrollHint(computed(() => isActiveEntry.value ? commentsScrollRef.value : null));
const dialogBodyRef = ref<HTMLElement | null>(null);

const getActiveCommentsContainer = (): HTMLElement | null => {
  if (isCompact.value) {
    return dialogBodyRef.value;
  }
  return commentsScrollRef.value;
};

let scrollCleanup: (() => void) | null = null;

const commentsSentinelRef = ref<HTMLElement | null>(null);
let commentsObserver: IntersectionObserver | null = null;

const teardownCommentsObserver = () => {
  commentsObserver?.disconnect();
  commentsObserver = null;
};

const setupCommentsObserver = () => {
  if (!import.meta.client) return;
  teardownCommentsObserver();
  if (!isActiveEntry.value) return;
  const sentinel = commentsSentinelRef.value;
  const root = getActiveCommentsContainer();
  if (!sentinel || !root) return;

  commentsObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        if (commentsHasNext.value && !commentsLoading.value) {
          void loadComments();
        }
      }
    },
    { root, rootMargin: "240px 0px" },
  );
  commentsObserver.observe(sentinel);
};

watch(
  [commentsSentinelRef, commentsVisible, isCompact, commentsLoading, commentsHasNext],
  () => {
    nextTick(() => {
      setupCommentsObserver();
    });
  },
  { flush: "post" },
);

const onContainerScroll = () => {
  if (!isActiveEntry.value) return;
  const container = getActiveCommentsContainer();
  if (!container || !comments.value.length || !props.postId || loading.value) return;

  // 阅读位置只按从旧到新记：进帖子时总是从最早开始，恢复的也是那条时间线上的位置。
  if (commentSort.value === "oldest") {
    const res = findTopVisibleComment(container, comments.value);
    if (!res) return;

    if (res.isTop) {
      commentHistory.clearRecord(props.postId);
    } else if (res.commentId && (res.floor == null || res.floor > 1)) {
      commentHistory.saveRecord(props.postId, {
        commentId: res.commentId,
        floor: res.floor,
        authorName: res.authorName,
      });
    }
  }

  // 触底隐式续拉保底
  if (commentsHasNext.value && !commentsLoading.value) {
    const distToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distToBottom < 300) {
      void loadComments();
    }
  }
};

const debouncedScroll = useDebounceFn(onContainerScroll, 250);

const attachScrollListeners = () => {
  scrollCleanup?.();
  if (!isActiveEntry.value) return;
  const c1 = commentsScrollRef.value;
  const c2 = dialogBodyRef.value;
  c1?.addEventListener("scroll", debouncedScroll, { passive: true });
  c2?.addEventListener("scroll", debouncedScroll, { passive: true });
  scrollCleanup = () => {
    c1?.removeEventListener("scroll", debouncedScroll);
    c2?.removeEventListener("scroll", debouncedScroll);
  };
};

watch(commentsVisible, (visible) => {
  if (visible) {
    nextTick(() => {
      attachScrollListeners();
    });
  }
});

// 同委托切换 commentId（如从通知再打开同一篇委托的另一条评论）时重新定位
watch(targetCommentId, () => {
  if (targetCommentId.value && post.value?.id === props.postId && isActiveEntry.value) {
    void seek();
  }
});

/** 正文渲染后再拉评论 / 定位目标评论，避免与入场动画、骨架屏切换抢主线程 */
const scheduleSeek = (request: PostRequest) => {
  if (!import.meta.client) return;
  const run = () => {
    if (!isCurrentPostRequest(request)) return;
    const historyRecord = !props.targetCommentId && commentHistory.autoRestore.value
      ? commentHistory.getRecord(props.postId)
      : null;
    if (historyRecord?.commentId && (historyRecord.floor == null || historyRecord.floor > 1)) {
      void seek(historyRecord.commentId, { highlight: false, behavior: "smooth" });
    } else {
      void seek();
    }
  };
  requestAnimationFrame(() => {
    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(run, { timeout: 500 });
    } else {
      setTimeout(run, 16);
    }
  });
};

const recordView = async (request: PostRequest) => {
  const viewedPost = post.value;
  if (!isCurrentPostRequest(request) || viewedPost?.id !== request.postId) return;
  try {
    const views = await api.recordArticleView(request.postId);
    if (isCurrentPostRequest(request) && post.value === viewedPost && typeof views === "number") {
      viewedPost.views = views;
    }
  } catch {
  }
};

const sendComment = async () => {
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  if (commentImages.hasPendingUploads.value) {
    message.warning("图片上传中，请稍候");
    return;
  }
  if (commentImages.hasErroredUploads.value) {
    message.warning("有图片上传失败，请重试或移除后再发送");
    return;
  }
  if (!hasCommentContent.value) return;

  sendingComment.value = true;
  const isReply = !!replyTarget.value;
  try {
    // serializeWith 把显示串里的 mention 段还原成 `@[name](docId)` token，
    // 表情占位符还原成 `:ik-xxx:` token
    const serialized = emoteInsert.serializeWith(mention.mentions.value).trim();
    const created = await api.addPostComment({
      postId: props.postId,
      content: serialized,
      authorDocumentId: auth.user?.authorId,
      replyToId: replyTarget.value?.id,
      isAnonymous: commentAnonymous.value || undefined,
      images: commentImages.uploadedImageIds.value,
    });
    countNewReply([comments.value, featuredComments.value], created);
    // 新评论排在最新的一头：从旧到新时已经翻到底就直接接上，从新到旧时停在最上面就插到最前，
    // 否则等用户翻到那里再加载。
    if (!comments.value.some((c) => c.id === created.id)) {
      if (commentSort.value === "newest") {
        if (!commentsPrevCursor.value) comments.value.unshift(created);
      } else if (!commentsHasNext.value) {
        comments.value.push(created);
      }
    }
    const el = commentInputBoxRef.value?.querySelector("textarea, input") as HTMLTextAreaElement | null;
    if (el) {
      el.value = "";
      el.blur();
    }
    newComment.value = "";
    mention.reset();
    emoteInsert.reset();
    commentImages.clearUploads();
    commentInputFocused.value = false;
    commentAnonymous.value = false;
    replyTarget.value = null;
    emotePickerVisible.value = false;
    syncCommentInputHeight();
    if (post.value) {
      post.value.commentsCount = (post.value.commentsCount ?? 0) + 1;
    }
    message.success(isReply ? "回复发送成功" : "评论发送成功");
    if (!commentsHasNext.value) void nextTick(() => jumpToComment(created.id));
  } catch (err) {
    message.error(resolveErrorMessage(err, "发送失败"));
  } finally {
    sendingComment.value = false;
  }
};

const openCommentImagePicker = () => {
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  commentImages.openImagePicker();
};

const likeArticle = async () => {
  if (!post.value) return;
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  try {
    const result = await api.toggleLike("article", post.value.id, !!post.value.liked);
    post.value.liked = result.liked;
    post.value.likesCount = result.likesCount;
    message.success(result.liked ? "已点赞（￣︶￣）↗　" : "已取消点赞(；′⌒`)");
  } catch (err) {
    message.error(resolveErrorMessage(err, "点赞失败"));
  }
};

const tripling = ref(false);
const tripleCharge = ref({ progress: 0, active: false });
const onTripleCharge = (payload: { progress: number; active: boolean }) => {
  tripleCharge.value = payload;
};

const handleTriple = async () => {
  if (!post.value) return;
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  if (tripling.value) return;
  tripling.value = true;

  const willCoin =
    !post.value.hasGivenDenny &&
    !isOwner.value &&
    !post.value.isAnonymous;
  if (willCoin) window.dispatchEvent(new CustomEvent("ik:denny-decrement"));

  try {
    const result = await api.tripleAction(post.value.id);
    post.value.liked = result.liked;
    post.value.likesCount = result.likesCount;
    post.value.favorited = result.favorited;
    post.value.favoritesCount = result.favoritesCount;
    post.value.dennyCount = result.dennyCount;
    if (result.coinGiven) post.value.hasGivenDenny = true;

    if (typeof result.newBalance === "number")
      window.dispatchEvent(
        new CustomEvent("ik:denny-updated", { detail: result.newBalance }),
      );
    else
      window.dispatchEvent(new CustomEvent("ik:denny-updated"));

    message.success("三连成功！(￣︶￣)↗");
  } catch (err) {
    window.dispatchEvent(new CustomEvent("ik:denny-updated"));
    message.error(resolveErrorMessage(err, "三连失败"));
  } finally {
    tripling.value = false;
  }
};

const givingDenny = ref(false);

const giveDenny = async () => {
  if (!post.value) return;
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  if (isOwner.value) {
    message.warning("不能给自己的委托投币");
    return;
  }
  if (post.value.isAnonymous) {
    message.warning("匿名委托不支持投币");
    return;
  }
  if (post.value.hasGivenDenny) {
    message.warning("已投过币，无法重复投币");
    return;
  }

  const prevHasGiven = post.value.hasGivenDenny;
  const prevDennyCount = post.value.dennyCount ?? 0;

  post.value.hasGivenDenny = true;
  post.value.dennyCount = prevDennyCount + 1;
  givingDenny.value = true;

  window.dispatchEvent(new CustomEvent("ik:denny-decrement"));

  try {
    const result = await api.giveDennyToArticle(post.value.id);
    if (result?.success) {
      message.success("投币成功");
      if (typeof result.articleDennyCount === "number") {
        post.value.dennyCount = result.articleDennyCount;
      }
      if (typeof result.newBalance === "number") {
        window.dispatchEvent(new CustomEvent("ik:denny-updated", { detail: result.newBalance }));
      } else {
        window.dispatchEvent(new CustomEvent("ik:denny-updated"));
      }
    } else {
      throw new Error("投币失败");
    }
  } catch (err) {
    post.value.hasGivenDenny = prevHasGiven;
    post.value.dennyCount = prevDennyCount;
    window.dispatchEvent(new CustomEvent("ik:denny-updated"));
    message.error(resolveErrorMessage(err, "投币失败"));
  } finally {
    givingDenny.value = false;
  }
};

const focusCommentInput = () => {
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  emotePickerVisible.value = false;
  ensureMentionInputInitialized();
  commentInputFocused.value = true;
  const input = commentInputBoxRef.value?.querySelector("textarea, input") as HTMLElement | null;
  input?.focus();
  syncCommentInputHeight();
};

const handleCommentInputFocus = (e: FocusEvent) => {
  if (!auth.isLogin) {
    (e.target as HTMLElement)?.blur();
    loginDialog.open();
    return;
  }
  emotePickerVisible.value = false;
  ensureMentionInputInitialized();
  commentInputFocused.value = true;
};

const cancelComment = () => {
  const el = commentInputBoxRef.value?.querySelector("textarea, input") as HTMLTextAreaElement | null;
  if (el) {
    el.value = "";
    el.blur();
  }
  newComment.value = "";
  mention.reset();
  emoteInsert.reset();
  commentImages.clearUploads();
  commentInputFocused.value = false;
  commentAnonymous.value = false;
  replyTarget.value = null;
  emotePickerVisible.value = false;
  syncCommentInputHeight();
};

const toggleAnonymous = () => {
  commentAnonymous.value = !commentAnonymous.value;
};

const startReply = (comment: Comment) => {
  replyTarget.value = {
    id: comment.id,
    authorName: comment.author?.name || "匿名用户",
  };
  focusCommentInput();
};

const isOwner = computed(() => post.value?.isOwner === true);
const isAdmin = computed(() => auth.isLogin && auth.user?.isAdmin === true);
const canPin = computed(() => auth.isLogin && (isOwner.value || isAdmin.value));
const canAccept = computed(() => auth.isLogin && (isOwner.value || isAdmin.value));
// 所在分区是否开启评论采纳（服务端按分区开关下发），与身份权限 canAccept 分开传递。
const acceptEnabled = computed(() => post.value?.allowCommentAccept === true);

const deletingArticle = ref(false);
const pinningArticle = ref(false);

const handlePinArticle = async () => {
  if (!post.value?.id) return;
  try {
    pinningArticle.value = true;
    if (post.value.isPinned) {
      await api.unpinArticle(post.value.id);
      message.success("已取消置顶");
    } else {
      await api.pinArticle(post.value.id);
      message.success("已置顶");
    }
    await loadPost();
  } catch (err) {
    message.error(resolveErrorMessage(err, post.value.isPinned ? "取消置顶失败" : "置顶失败"));
  } finally {
    pinningArticle.value = false;
  }
};

const handleDeleteArticle = async () => {
  if (!post.value?.id) return;
  const ok = await confirmDialog.open({ title: "删除委托", message: "确定删除这篇委托？删除后不可恢复。", confirmText: "删除", danger: true });
  if (!ok) return;
  deletingArticle.value = true;
  try {
    const deletedId = post.value.id;
    await api.deleteArticle(deletedId);
    message.success("委托已删除");
    window.dispatchEvent(new CustomEvent("ik:article-deleted", { detail: deletedId }));
    emit("close");
  } catch (err) {
    message.error(resolveErrorMessage(err, "删除委托失败"));
  } finally {
    deletingArticle.value = false;
  }
};

const handleEditArticle = () => {
  if (!post.value?.id || !isOwner.value) return;
  // 不能先 emit("close")：postModal.close() 会 history.back()，
  // 随后的 popstate 会取消 navigateTo。路径变化时 app.vue 的
  // beforeResolve 守卫会在导航确认后自动 teardown 弹窗。
  navigateTo(`/create?edit=${post.value.id}`);
};

// 点击标签 chip 跳转到标签页。与 handleEditArticle 同理：直接 navigateTo，
// 路径变化触发 app.vue 的守卫自动收起弹窗，不先手动 close 以免 popstate 取消导航。
const goTag = (slug: string) => {
  if (!slug) return;
  navigateTo(`/tags/${encodeURIComponent(slug)}`);
};

const handleArticleMenuCommand = (command: string | number) => {
  if (command === "delete") {
    handleDeleteArticle();
  } else if (command === "edit") {
    handleEditArticle();
  } else if (command === "report") {
    handleReportArticle();
  } else if (command === "pin" || command === "unpin") {
    handlePinArticle();
  }
};

const handleReportArticle = () => {
  if (!post.value?.id) return;
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  reportDialog.open({ targetType: "article", targetId: post.value.id, targetLabel: "委托" });
};

const handleReportComment = (comment: Comment) => {
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  reportDialog.open({ targetType: "comment", targetId: comment.id, targetLabel: "评论" });
};

const favoriting = ref(false);

const favoriteArticle = async () => {
  if (!post.value) return;
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  if (favoriting.value) return;
  favoriting.value = true;
  try {
    const result = await api.toggleFavorite(post.value.id, !!post.value.favorited);
    post.value.favorited = result.favorited;
    post.value.favoritesCount = result.favoritesCount;
    message.success(result.favorited ? "已收藏" : "已取消收藏");
  } catch (err) {
    message.error(resolveErrorMessage(err, "收藏失败"));
  } finally {
    favoriting.value = false;
  }
};

const likeComment = async (comment: Comment) => {
  if (!auth.isLogin) {
    loginDialog.open();
    return;
  }
  try {
    const result = await api.toggleLike("comment", comment.id, !!comment.liked);
    comment.liked = result.liked;
    comment.likesCount = result.likesCount;
  } catch (err) {
    message.error(resolveErrorMessage(err, "评论点赞失败"));
  }
};

const handleDeleteComment = async (comment: Comment) => {
  const ok = await confirmDialog.open({
    title: "删除评论",
    message: "确定删除这条评论吗？此操作无法撤销。",
    confirmText: "删除",
    danger: true,
  });
  if (!ok) return;
  try {
    await api.deleteComment(comment.id);
    comments.value = removeFromTimeline(comments.value, comment);
    featuredComments.value = removeFromTimeline(featuredComments.value, comment);
    if (post.value) {
      post.value.commentsCount = Math.max(0, (post.value.commentsCount ?? 0) - 1);
    }
    message.success("评论已删除");
  } catch (err) {
    message.error(resolveErrorMessage(err, "删除评论失败"));
  }
};

const handlePinComment = async (comment: Comment) => {
  if (!props.postId) return;
  try {
    await api.pinComment(comment.id, props.postId);
    await refreshComments();
    message.success("评论已置顶");
  } catch (err) {
    message.error(resolveErrorMessage(err, "置顶失败"));
  }
};

const handleUnpinComment = async (comment: Comment) => {
  if (!props.postId) return;
  try {
    await api.unpinComment(comment.id, props.postId);
    await refreshComments();
    message.success("评论已取消置顶");
  } catch (err) {
    message.error(resolveErrorMessage(err, "取消置顶失败"));
  }
};

const handleAcceptComment = async (comment: Comment) => {
  if (!props.postId) return;
  try {
    await api.acceptComment(comment.id, props.postId);
    await refreshComments();
    message.success("已采纳");
  } catch (err) {
    message.error(resolveErrorMessage(err, "采纳失败"));
  }
};

const handleUnacceptComment = async (comment: Comment) => {
  if (!props.postId) return;
  try {
    await api.unacceptComment(comment.id, props.postId);
    await refreshComments();
    message.success("已取消采纳");
  } catch (err) {
    message.error(resolveErrorMessage(err, "取消采纳失败"));
  }
};

/* ── 关闭 / 键盘 ──────────────────────────────── */
const handleClose = () => {
  emotePickerVisible.value = false;
  emit("close");
};

const onBackdropClick = (e: MouseEvent) => {
  if (e.target === e.currentTarget) {
    handleClose();
  }
};

const focusTrapActive = computed(() => isActiveEntry.value &&
  !loginDialog.visible.value && !confirmDialog.state.visible && !reportDialog.state.visible &&
  !isAnyGalleryOpen.value && !commentImages.showImagePickerModal.value && !emotePickerVisible.value);

const onKeyDown = (e: KeyboardEvent) => {
  // 上层窗口 / 菜单拥有 Escape，不能同时关闭帖子或丢弃正在编辑的评论。
  if (e.key !== "Escape" || e.defaultPrevented || e.repeat || !focusTrapActive.value) return;
  e.preventDefault();
  if (isCommentEditorActive.value) cancelComment();
  else handleClose();
};

/* ── 浮层内切换委托时重放进场动画 ─────────────
   浮层已打开时点通知等跳转到另一条委托，只换 postId，组件不重新挂载，
   app.vue 外层的 <Transition> 不会触发，观感像「原地替换内容」。
   这里给弹窗主体 .ik-dialog 重放一次与瀑布流点开一致的滑入+淡入，
   背景遮罩保持不动，让新委托像「新帖子进场」而非替换。 */
const dialogRef = ref<HTMLElement | null>(null);
const focusOnOpen = (event: Event) => {
  event.preventDefault();
  if (!focusTrapActive.value) return;
  const target = dialogRef.value?.querySelector<HTMLElement>(".ik-dialog__close") ?? dialogRef.value;
  target?.focus({ preventScroll: true });
};
let switchResetTimer: ReturnType<typeof setTimeout> | null = null;
let switchAnimationEndsAt = 0;

const replaySwitchAnimation = () => {
  if (!import.meta.client) return;
  const el = dialogRef.value;
  if (!el) return;
  switchAnimationEndsAt = performance.now() + 200;
  // 重启 CSS animation 必须走「移除 class → 强制同步 reflow → 加回 class」三步：
  // 只靠 nextTick 切换 ref，移除与加回会落在同一绘制帧的微任务里被浏览器合并，
  // animation 属性无变化 → 动画不重放，观感退化为「原地替换内容」。
  el.classList.remove("ik-dialog--switching");
  void el.offsetWidth; // 强制 reflow，让浏览器确认上一轮动画已结束
  el.classList.add("ik-dialog--switching");
  if (switchResetTimer) clearTimeout(switchResetTimer);
  // 略大于动画时长后卸掉 class，避免残留影响后续布局
  switchResetTimer = setTimeout(() => {
    el.classList.remove("ik-dialog--switching");
    switchResetTimer = null;
  }, 240);
};

/* ── 当 postId 变化时重新加载 ─────────────── */
let viewGeneration = auth.generation;
const resetAndLoad = async () => {
  viewGeneration = auth.generation;
  cancelSeek();
  teardownCommentsObserver();
  cancelScheduledRevealWork();
  commentsVisible.value = false;
  isScrollable.value = false;
  marqueeReady.value = false;
  post.value = null;
  commentSort.value = "oldest";
  resetCommentTimeline();
  commentsLoaded.value = false;
  commentsLoading.value = false;
  currentCommentLoadId++;
  loadError.value = false;
  newComment.value = "";
  mention.reset();
  emoteInsert.reset();
  commentImages.clearUploads();
  commentInputFocused.value = false;
  replyTarget.value = null;
  loading.value = true;
  if (scrollRef.value) {
    scrollRef.value.scrollTop = 0;
  }
  if (detailScrollRef.value) {
    detailScrollRef.value.scrollTop = 0;
  }
  const request = await loadPost();
  if (!request || !isCurrentPostRequest(request)) return;
  if (!loadError.value && !(await waitForLoadedPreview(request))) return;
  if (!isCurrentPostRequest(request)) return;
  // 主体一拿到就解除骨架屏；评论与浏览数后台继续，不阻塞 UI。
  loading.value = false;
  void recordView(request);
  if (!loadError.value) {
    scheduleSeek(request);
  }
  scheduleRevealWork();
};

watch(
  [() => props.postId, () => auth.generation],
  ([newId], [oldId]) => {
    if (newId && isActiveEntry.value) {
      // postId 真的变了（通知等跳转到另一条委托）才重放进场动画；
      // 仅 generation 变化（重新登录刷新）不重放，避免无谓的闪动。
      if (oldId && newId !== oldId) replaySwitchAnimation();
      void resetAndLoad();
    }
  },
);

watch(newComment, () => {
  syncCommentInputHeight();
});

/**
 * 把真实 textarea 拿到并接上 mention 事件钩子。
 * 与 pages/post/[id].vue 完全对称。
 */
let teardownMentionListeners: (() => void) | null = null;
const attachMentionToTextarea = () => {
  if (!isActiveEntry.value) return;
  const root = commentInputBoxRef.value;
  if (!root) return;
  const ta = root.querySelector("textarea") as HTMLTextAreaElement | null;
  if (!ta) return;

  teardownMentionListeners?.();
  commentTextareaRef.value = ta;

  const onInput = () => {
    mention.refresh();
    emoteInsert.refresh();
  };
  const onKeyDownMention = (e: KeyboardEvent) => {
    mention.onKeyDown(e);
    emoteInsert.onKeyDown(e);
  };
  const onSelect = (e: Event) => {
    mention.refresh(e);
    emoteInsert.refresh();
  };

  ta.addEventListener("input", onInput);
  ta.addEventListener("keydown", onKeyDownMention);
  ta.addEventListener("click", onSelect);
  ta.addEventListener("keyup", onSelect);

  teardownMentionListeners = () => {
    ta.removeEventListener("input", onInput);
    ta.removeEventListener("keydown", onKeyDownMention);
    ta.removeEventListener("click", onSelect);
    ta.removeEventListener("keyup", onSelect);
  };
};

// 编辑器从 placeholder 模式切到激活模式时 z-input 会重新挂载，
// 需要重新查找真实 textarea 并 attach 一次。
watch(commentInputFocused, () => {
  nextTick(() => attachMentionToTextarea());
});

// 首次 focus / 点击 @ / 点击表情时实例化真实 mention/emote 逻辑，
// 并 attach 到真实 textarea。弹窗打开时只持有无副作用的默认 stub。
function ensureMentionInputInitialized() {
  if (mentionInputInitialized.value) return;
  Object.assign(mention, useMentionInput({
    text: newComment,
    textareaRef: commentTextareaRef,
    search: api.searchAuthors,
  }));
  Object.assign(emoteInsert, useEmoteInsert({
    text: newComment,
    textareaRef: commentTextareaRef,
    onInsert: (start, end, insertedLength) => {
      const delta = insertedLength - (end - start);
      mention.mentions.value = mention.mentions.value
        .filter((m) => m.end <= start || m.start >= end)
        .map((m) => (m.start >= end ? { ...m, start: m.start + delta, end: m.end + delta } : m));
    },
  }));
  mentionInputInitialized.value = true;
  attachMentionToTextarea();
}

// 每个历史条目由 KeepAlive 保留独立实例；离开前存 DOM 位置，返回时不重置草稿或评论。
let savedScroll: Array<{ element: HTMLElement; top: number; left: number }> = [];
let savedFocus: HTMLElement | null = null;
let wasDeactivated = false;
const saveViewPosition = () => {
  savedScroll = [scrollRef.value, detailScrollRef.value, commentsScrollRef.value, dialogBodyRef.value]
    .filter((element): element is HTMLElement => !!element)
    .map((element) => ({ element, top: element.scrollTop, left: element.scrollLeft }));
  const activeElement = document.activeElement;
  savedFocus = activeElement instanceof HTMLElement && dialogRef.value?.contains(activeElement)
    ? activeElement : null;
  // 离线容器没有布局，继续观察尺寸会把轮播重置到第一张。
  destroyEmbla();
};
watch(isActiveEntry, (active, previous) => {
  // 必须在 KeepAlive 把节点移到离线容器之前读取，尤其是移动端整列滚动。
  if (!active && previous) saveViewPosition();
}, { flush: "sync" });

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
  void resetAndLoad();
});

onDeactivated(() => {
  wasDeactivated = true;
  currentPostLoadId++;
  currentCommentLoadId++;
  commentsLoading.value = false;
  commentsLoadingEarlier.value = false;
  cancelScheduledRevealWork();
  teardownCommentsObserver();
  scrollCleanup?.();
  scrollCleanup = null;
  teardownMentionListeners?.();
  teardownMentionListeners = null;
  window.removeEventListener("keydown", onKeyDown);
  destroyPreview();
  emotePickerVisible.value = false;
  mention.pickerVisible.value = false;
  commentImages.showImagePickerModal.value = false;
  if (switchResetTimer) clearTimeout(switchResetTimer);
  switchResetTimer = null;
  switchAnimationEndsAt = 0;
  dialogRef.value?.classList.remove("ik-dialog--switching");
});

onActivated(async () => {
  // KeepAlive 首次挂载也调用 activated，首屏加载由 mounted 负责。
  if (!wasDeactivated) return;
  window.addEventListener("keydown", onKeyDown);
  const reuseComments = viewGeneration === auth.generation && !loading.value;
  if (viewGeneration !== auth.generation || loading.value) {
    await resetAndLoad();
  } else {
    commentsVisible.value = true;
    isScrollable.value = true;
    marqueeReady.value = true;
    if (post.value?.title) postModal.setTitle(post.value.title);
  }
  await nextTick();
  if (!isActiveEntry.value) return;
  if (emblaRef.value && !emblaApi.value) initEmbla(emblaRef.value);
  for (const { element, top, left } of savedScroll) {
    element.scrollTop = top;
    element.scrollLeft = left;
  }
  detailHint.update();
  commentsHint.update();
  setupCommentsObserver();
  attachScrollListeners();
  attachMentionToTextarea();
  if (reuseComments && !loadError.value) {
    const resumed = resumeSeek();
    if (resumed) void resumed;
    else if (!commentsLoaded.value) {
      scheduleSeek({ loadId: currentPostLoadId, postId: props.postId, generation: auth.generation });
    }
  }
  if (focusTrapActive.value) {
    const target = savedFocus?.isConnected ? savedFocus : dialogRef.value?.querySelector<HTMLElement>(".ik-dialog__close");
    target?.focus({ preventScroll: true });
  }
});

onBeforeUnmount(() => {
  isUnmounted = true;
  currentPostLoadId++;
  isCommentLoadingCancelled = true;
  currentCommentLoadId++;
  commentsLoading.value = false;
  commentsHasNext.value = false;
  window.removeEventListener("keydown", onKeyDown);
  destroyPreview();
  teardownMentionListeners?.();
  teardownMentionListeners = null;
  cancelScheduledRevealWork();
  if (switchResetTimer) {
    clearTimeout(switchResetTimer);
    switchResetTimer = null;
  }
  scrollCleanup?.();
  scrollCleanup = null;
  teardownCommentsObserver();
  isScrollable.value = false;
});
</script>

<template>
  <div class="ik-overlay" @click="onBackdropClick">
      <!-- 独立背景层：用 opacity 过渡替代 .ik-overlay 的 background-color 过渡，避免全屏 paint -->
      <div class="ik-overlay__backdrop" aria-hidden="true"></div>

      <!-- ── 斜线纹理背景（ZZZ PatternPainter） ──── -->
      <div class="ik-overlay__stripe" aria-hidden="true"></div>

      <!-- ── 弹窗主体 ────────────────────────────── -->
      <FocusScope
        as-child
        :loop="focusTrapActive"
        :trapped="focusTrapActive"
        @mount-auto-focus="focusOnOpen"
        @unmount-auto-focus="$event.preventDefault()"
      >
      <div ref="dialogRef" class="ik-dialog" role="dialog" aria-modal="true" :aria-label="headerTitle || '帖子详情'" :class="{ 'ik-dialog--emote-open': emotePickerVisible }">
        <!-- 外边框（半透明白色，三圆角） -->
        <div class="ik-dialog__outer">
          <!-- 内边框（纯黑，三圆角） -->
          <div class="ik-dialog__inner">
            <!-- ── Header Bar ─────────────────────── -->
            <div class="ik-dialog__header">
              <div class="ik-dialog__header-left">
                <UserHoverCard :author-id="headerAuthor?.documentId" :member-no="headerAuthor?.memberNo" :clickable="!!headerAuthor?.documentId">
                  <div class="ik-dialog__avatar-wrap">
                    <div class="ik-dialog__avatar-shell">
                      <img
                        v-if="headerAuthor"
                        :src="headerAuthor.avatar || '/images/default-avatar.webp'"
                        :alt="headerAuthor.name || ''"
                        class="ik-dialog__avatar"
                        @error="($event.target as HTMLImageElement).src = '/images/default-avatar.webp'"
                      />
                      <div v-else class="ik-skel" style="width:100%;height:100%;border-radius:999px"></div>
                    </div>
                    <span v-if="headerAuthor?.level && headerAuthor?.documentId" class="ik-dialog__level">{{ headerAuthor.level }}</span>
                  </div>
                </UserHoverCard>
                <div v-if="headerAuthor" class="ik-dialog__author-info">
                  <div class="ik-dialog__author-row">
                    <UserHoverCard :author-id="headerAuthor.documentId" :member-no="headerAuthor?.memberNo" :clickable="!!headerAuthor?.documentId">
                      <span class="ik-dialog__author-name">
                        {{ headerAuthor.name || "匿名用户" }}
                      </span>
                    </UserHoverCard>
                  </div>
                  <span v-if="post" class="ik-dialog__popularity">
                    <HeartIcon class="ik-dialog__popularity-icon" aria-hidden="true" />{{ postLikeCount }}
                  </span>
                  <div v-else class="ik-skel" style="width:60px;height:16px;border-radius:3px"></div>
                </div>
                <div v-else class="ik-dialog__author-info">
                  <div class="ik-skel" style="width:100px;height:20px;border-radius:3px"></div>
                  <div class="ik-skel" style="width:60px;height:16px;border-radius:3px"></div>
                </div>
              </div>
              <button class="ik-dialog__close" aria-label="关闭" @click="handleClose">
                <img src="/images/close-btn.webp" alt="关闭" class="ik-dialog__close-img" draggable="false" />
              </button>
              <!-- ── Gallery Loading Progress ────────── -->
              <div class="ik-dialog__gallery-progress" :class="{ 'is-active': isGalleryLoading }">
                <div class="ik-dialog__gallery-progress-bar" :style="{ width: `${galleryProgress}%` }" />
              </div>
            </div>

            <!-- ── Content Area ───────────────────── -->
            <div class="ik-dialog__main">
              <IkZzzMarquee :paused="!marqueeReady || !isActiveEntry" />

            <div v-show="loading" class="ik-dialog__body" :class="{ 'ik-dialog__body--scrollable': isScrollable }">
              <!-- 骨架屏：左栏 -->
              <div class="ik-dialog__left">
                <div class="ik-dialog__left-scroll">
                  <div class="ik-dialog__cover-wrap">
                    <div
                      class="ik-dialog__cover-border"
                      :style="props.coverHint ? { '--ik-cover-ratio': String(props.coverHint) } : {}"
                    >
                      <div v-if="previewCover" class="ik-dialog__cover-preview">
                        <img
                          :src="previewCover"
                          alt=""
                          class="ik-dialog__cover-preview-image"
                          aria-hidden="true"
                          decoding="async"
                        />
                      </div>
                      <div v-else class="ik-skel ik-dialog__cover-skel" aria-hidden="true"></div>
                    </div>
                  </div>
                  <div class="ik-dialog__detail-frame">
                  <div class="ik-dialog__detail">
                    <h1 v-if="headerTitle" class="ik-dialog__title"><span v-if="headerCategory" class="ik-dialog__title-cat">[{{ headerCategory.name }}]</span>{{ headerTitle }}</h1>
                    <div v-else class="ik-skel ik-skel--title"></div>
                    <div class="ik-skel ik-skel--line" style="width:100%"></div>
                    <div class="ik-skel ik-skel--line" style="width:90%"></div>
                    <div class="ik-skel ik-skel--line" style="width:75%"></div>
                    <div class="ik-skel ik-skel--line" style="width:60%"></div>
                  </div>
                  </div>
                </div>
              </div>
              <!-- 骨架屏：右栏（与评论自身骨架保持一致） -->
              <div class="ik-dialog__right">
                <div style="flex:1;padding:16px;overflow:hidden">
                  <div class="ik-dialog__comments-toolbar-spacer" aria-hidden="true"></div>
                  <div v-for="n in 5" :key="n" style="display:flex;gap:12px;padding:14px 0" :style="n > 1 ? 'border-top:1px solid #1e1e1e' : ''">
                    <div class="ik-skel" style="width:36px;height:36px;border-radius:999px;flex-shrink:0"></div>
                    <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:6px">
                      <div class="ik-skel" style="width:80px;height:14px;border-radius:3px"></div>
                      <div class="ik-skel" style="width:95%;height:14px;border-radius:3px"></div>
                      <div class="ik-skel" style="width:60%;height:14px;border-radius:3px"></div>
                    </div>
                  </div>
                </div>
                <div class="ik-dialog__actions">
                  <div class="ik-skel ik-skel--actions-bar"></div>
                </div>
              </div>
            </div>

            <div v-if="!loading && loadError" class="ik-dialog__error">
              加载失败，请关闭后重试
            </div>

            <template v-if="post">
              <!-- 桌面端：双栏布局 -->
              <div v-show="!loading" ref="dialogBodyRef" class="ik-dialog__body" :class="{ 'ik-dialog__body--scrollable': isScrollable, 'ik-dialog__body--emote-open': emotePickerVisible }">
                <!-- 左栏：封面 + 正文 -->
                <div class="ik-dialog__left">
                  <div class="ik-dialog__left-scroll" ref="scrollRef">
                    <!-- 封面 / 视频 -->
                    <div class="ik-dialog__cover-wrap">
                      <div
                        class="ik-dialog__cover-border"
                        :style="{ '--ik-cover-ratio': String(coverAspectRatio) }"
                      >
                        <!-- 单张封面 -->
                        <template v-if="hasCovers && covers.length === 1">
                          <div v-if="coverPreviewSrc(0)" class="ik-dialog__cover-preview">
                            <img
                              :ref="setLoadedPreviewImage"
                              :src="coverPreviewSrc(0)"
                              alt=""
                              class="ik-dialog__cover-preview-image"
                              aria-hidden="true"
                              :decoding="isCompact ? 'async' : 'sync'"
                            />
                          </div>
                          <img
                            :src="firstCover ? coverDisplaySrc(firstCover.url) : DEFAULT_COVER_IMAGE"
                            :alt="post.title"
                            class="ik-dialog__cover"
                            loading="eager"
                            decoding="async"
                            referrerpolicy="no-referrer"
                            @load="onCoverImageLoad(0)"
                            @click="openCoverPreview(0)"
                            @error="onCoverImageLoad(0); ($event.target as HTMLImageElement).src = DEFAULT_COVER_IMAGE"
                          />
                          <div
                            v-if="!isCoverImageLoaded(0) && !coverPreviewSrc(0)"
                            class="ik-skel ik-dialog__cover-skel"
                            aria-hidden="true"
                          ></div>
                        </template>

                        <!-- 多图轮播 -->
                        <template v-else-if="hasCovers">
                          <div
                            ref="emblaRef"
                            class="ik-dialog__cover-scroller"
                            @wheel="onCoverWheel"
                          >
                            <div class="ik-dialog__cover-track">
                              <div
                                v-for="(c, i) in covers"
                                :key="c.url + i"
                                class="ik-dialog__cover-slide"
                              >
                                <div
                                  v-if="coverPreviewSrc(i)"
                                  class="ik-dialog__cover-preview"
                                >
                                  <img
                                    :ref="i === 0 ? setLoadedPreviewImage : undefined"
                                    :src="isCoverNearby(i) ? coverPreviewSrc(i) : undefined"
                                    alt=""
                                    class="ik-dialog__cover-preview-image"
                                    aria-hidden="true"
                                    :decoding="i === 0 && !isCompact ? 'sync' : 'async'"
                                  />
                                </div>
                                <img
                                  v-if="isCoverNearby(i)"
                                  :src="slideDisplaySrc(c)"
                                  :alt="`${post.title} - ${i + 1}`"
                                  class="ik-dialog__cover"
                                  loading="eager"
                                  decoding="async"
                                  draggable="false"
                                  referrerpolicy="no-referrer"
                                  @load="onCoverImageLoad(i)"
                                  @click="openCoverPreview(i)"
                                  @error="onCoverImageLoad(i); ($event.target as HTMLImageElement).src = DEFAULT_COVER_IMAGE"
                                />
                                <div
                                  v-if="!isCoverImageLoaded(i) && (!isCoverNearby(i) || !coverPreviewSrc(i))"
                                  class="ik-skel ik-dialog__cover-skel"
                                  :class="{ 'ik-dialog__cover-skel--offscreen': i !== coverIndex }"
                                  aria-hidden="true"
                                ></div>
                              </div>
                            </div>
                          </div>

                          <CoverPager
                            :index="coverIndex"
                            :total="covers.length"
                            @go="goCover"
                          />
                        </template>

                        <!-- 无图片封面但有 B 站视频：在封面区域直接放播放器 -->
                        <template v-else-if="post.externalVideos?.length">
                          <BilibiliPlayer
                            v-for="(video, idx) in post.externalVideos"
                            :key="`video-${idx}`"
                            :video="video"
                          />
                        </template>

                        <!-- 默认占位图 -->
                        <template v-else>
                          <img
                            :src="DEFAULT_COVER_IMAGE"
                            alt="default cover"
                            class="ik-dialog__cover"
                            loading="eager"
                            decoding="async"
                            referrerpolicy="no-referrer"
                            @load="onCoverImageLoad(0)"
                          />
                          <div
                            v-if="!isCoverImageLoaded(0)"
                            class="ik-skel ik-dialog__cover-skel"
                            aria-hidden="true"
                          ></div>
                        </template>
                      </div>
                    </div>

                    <!-- 正文：桌面端在封面右侧单独滚动 -->
                    <div class="ik-dialog__detail-frame">
                    <div ref="detailScrollRef" class="ik-dialog__detail">
                      <div v-if="post.isHidden" class="ik-dialog__hidden-banner" role="alert">
                        <EyeSlashIcon class="ik-dialog__hidden-icon" aria-hidden="true" />
                        <span>该委托因收到举报已被隐藏，仅你自己可见。如有异议请联系管理员。</span>
                      </div>
                      <h1 class="ik-dialog__title">
                        <span v-if="post.category" class="ik-dialog__title-cat">[{{ post.category.name }}]</span>{{ post.title }}
                      </h1>
                      <div
                        v-if="bodyHasContent"
                        class="ik-dialog__content"
                        v-html="bodyHtml"
                        @click="openPostLink($event, postModal.open)"
                      ></div>
                      <p v-else-if="!post.externalVideos?.length" class="ik-dialog__content" style="color: #808080">
                        啥都木有¯\(°_o)/¯
                      </p>
                      <div v-if="hasCovers && post.externalVideos?.length" class="ik-dialog__videos">
                        <BilibiliPlayer
                          v-for="(video, idx) in post.externalVideos"
                          :key="`video-${idx}`"
                          :video="video"
                        />
                      </div>
                      <div v-if="post.tags?.length" class="ik-dialog__tags">
                        <button
                          v-for="tag in post.tags"
                          :key="tag.slug"
                          type="button"
                          class="ik-dialog__tag"
                          @click="goTag(tag.slug)"
                        >#{{ tag.name }}</button>
                      </div>
                      <p v-if="headerCreatedAt || post.editedAt" class="ik-dialog__time">
                        <template v-if="headerCreatedAt">{{ formatTime(headerCreatedAt) }}</template>
                        <template v-if="post.editedAt"> · 编辑于 {{ formatTime(post.editedAt) }}</template>
                      </p>
                    </div>
                    <ScrollHintArrows v-if="!isCompact" :up="detailHint.up.value" :down="detailHint.down.value" />
                    </div>
                  </div>
                </div>

                <!-- 右栏：评论 + 操作栏 -->
                <div class="ik-dialog__right">
                  <div class="ik-dialog__comments-frame">
                  <ScrollHintArrows v-if="!isCompact" :up="commentsHint.up.value" :down="commentsHint.down.value" />
                  <div class="ik-dialog__comments-scroll" ref="commentsScrollRef">
                    <div ref="commentsInnerRef" class="ik-dialog__comments-inner">
                      <CommentsToolbar
                        v-if="postCommentCount > 1"
                        :count="postCommentCount"
                        :sort="commentSort"
                        :disabled="showCommentsSkeleton"
                        @update:sort="changeCommentSort"
                      />
                      <h2 v-else class="ik-dialog__comments-heading">评论 <span>{{ postCommentCount }}</span></h2>
                      <div v-if="showCommentsSkeleton">
                        <div v-for="n in 5" :key="n" style="display:flex;gap:12px;padding:14px 0" :style="n > 1 ? 'border-top:1px solid #1e1e1e' : ''">
                          <div class="ik-skel" style="width:36px;height:36px;border-radius:999px;flex-shrink:0"></div>
                          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:6px">
                            <div class="ik-skel" style="width:80px;height:14px;border-radius:3px"></div>
                            <div class="ik-skel" style="width:95%;height:14px;border-radius:3px"></div>
                            <div class="ik-skel" style="width:60%;height:14px;border-radius:3px"></div>
                          </div>
                        </div>
                      </div>
                      <div v-else-if="showCommentsEmpty" class="ik-empty" style="padding: 40px 0">啥都木有¯\(°_o)/¯</div>
                      <template v-if="commentsVisible">
                        <CommentItem
                          v-for="comment in featuredComments"
                          :key="`featured-${comment.id}`"
                          featured
                          :highlighted-comment-id="highlightedCommentId"
                          :comment="comment"
                          :current-user-author-id="auth.user?.authorId"
                          :post-author-id="post.author?.documentId"
                          :is-post-owner="auth.isLogin && isOwner"
                          :can-pin="canPin"
                          :can-accept="canAccept"
                          :accept-enabled="acceptEnabled"
                          @like-comment="likeComment"
                          @reply-comment="startReply"
                          @jump-to="jumpToComment"
                          @delete-comment="handleDeleteComment"
                          @report-comment="handleReportComment"
                          @pin-comment="handlePinComment"
                          @unpin-comment="handleUnpinComment"
                          @accept-comment="handleAcceptComment"
                          @unaccept-comment="handleUnacceptComment"
                        />
                        <button
                          v-if="commentsPrevCursor"
                          type="button"
                          class="ik-comments-earlier"
                          :disabled="commentsLoadingEarlier"
                          @click="loadEarlierComments"
                        >
                          {{ commentsLoadingEarlier ? "加载中…" : commentSort === "newest" ? "加载更新的评论" : "加载更早的评论" }}
                        </button>
                        <CommentItem
                          v-for="(comment, idx) in comments"
                          :key="comment.id"
                          :comment="comment"
                          :index="idx"
                          :current-user-author-id="auth.user?.authorId"
                          :post-author-id="post.author?.documentId"
                          :is-post-owner="auth.isLogin && isOwner"
                          :can-pin="canPin"
                          :can-accept="canAccept"
                          :accept-enabled="acceptEnabled"
                          :highlighted-comment-id="highlightedCommentId"
                          @like-comment="likeComment"
                          @reply-comment="startReply"
                          @jump-to="jumpToComment"
                          @delete-comment="handleDeleteComment"
                          @report-comment="handleReportComment"
                          @pin-comment="handlePinComment"
                          @unpin-comment="handleUnpinComment"
                          @accept-comment="handleAcceptComment"
                          @unaccept-comment="handleUnacceptComment"
                        />
                        <div
                          v-if="comments.length || featuredComments.length"
                          ref="commentsSentinelRef"
                          class="ik-comments-sentinel"
                        >
                          <div v-if="commentsLoading || !commentsHasNext" class="ik-scroll-footer">
                            <img v-if="commentsLoading" class="ik-scroll-gif" src="/images/Bangboo.gif" alt="加载中" />
                            <span v-else class="ik-comments-end">— 已无更多评论 —</span>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  </div>

                  <!-- 底部操作栏 -->
                  <div class="ik-dialog__actions" :class="{ 'ik-dialog__actions--active': isCommentEditorActive, 'ik-dialog__actions--emote-open': emotePickerVisible }">
                    <!-- 评论输入 -->
                    <div class="ik-engage-bar">
                      <div class="ik-engage-bar__main">
                        <div ref="commentInputBoxRef" class="ik-engage-bar__content-edit" @click="focusCommentInput">
                          <z-input
                            v-model="newComment"
                            type="textarea"
                            class="ik-engage-bar__textarea"
                            placeholder=""
                            @focus="handleCommentInputFocus"
                            @input="syncCommentInputHeight"
                            @keydown.enter.exact.prevent="sendComment"
                          />
                          <div v-if="commentImages.uploadTasks.value.length" class="ik-engage-bar__attachments">
                            <div
                              v-for="(task, index) in commentImages.uploadTasks.value"
                              :key="task.localId"
                              class="ik-engage-bar__attachment"
                              :class="`is-${task.status}`"
                            >
                              <img
                                :src="task.serverUrl || task.previewUrl"
                                :alt="task.filename"
                                class="ik-engage-bar__attachment-thumb"
                              />
                              <div v-if="task.status === 'uploading' || task.status === 'pending' || task.status === 'compressing'" class="ik-engage-bar__attachment-progress">
                                <span>{{ Math.round(task.progress) }}%</span>
                              </div>
                              <button
                                v-else-if="task.status === 'error'"
                                type="button"
                                class="ik-engage-bar__attachment-error"
                                @click.stop="commentImages.retryUpload(task)"
                              >
                                <span>{{ task.error || '上传失败' }}<br>点击重试</span>
                              </button>
                              <button
                                type="button"
                                class="ik-engage-bar__attachment-remove"
                                aria-label="移除"
                                @click.stop="commentImages.removeUpload(index)"
                              >✕</button>
                            </div>
                          </div>
                          <!-- @ 提及高亮叠加层：teleport 到 textarea 父级，pointer-events:none -->
                          <MentionHighlightOverlay
                            v-if="mentionInputInitialized"
                            :target="commentTextareaRef"
                            :text="newComment"
                            :mentions="mention.mentions.value"
                            :emotes="emoteInsert.emotes.value"
                          />
                          <div v-if="replyTarget && isCommentEditorActive" class="ik-engage-bar__reply-hint">
                            <span>回复 {{ replyTarget.authorName }}</span>
                            <button type="button" class="ik-engage-bar__reply-close" @click="replyTarget = null">✕</button>
                          </div>
                          <div v-if="!isCommentEditorActive && !newComment.trim() && !emoteInsert.hasEmotes.value && !commentImages.uploadTasks.value.length" class="ik-engage-bar__placeholder">
                            <img
                              :src="auth.user?.avatar || '/images/default-avatar.webp'"
                              alt=""
                              class="ik-engage-bar__placeholder-avatar"
                              @error="($event.target as HTMLImageElement).src = '/images/default-avatar.webp'"
                            />
                            <span>说点什么...</span>
                          </div>
                        </div>

                        <div class="ik-engage-bar__interact-container">
                          <div class="ik-engage-bar__buttons">
                            <TripleActionButton
                              :liked="post.liked"
                              :likes-count="postLikeCount"
                              :can-triple="auth.isLogin && !isOwner"
                              :busy="tripling"
                              @like="likeArticle"
                              @triple="handleTriple"
                              @charge="onTripleCharge"
                            />
                            <button
                              type="button"
                              class="ik-engage-bar__action"
                              :class="{ 'ik-engage-bar__action--active': post.hasGivenDenny }"
                              :disabled="isOwner || post.isAnonymous || givingDenny"
                              :title="isOwner ? '不能给自己的委托投币' : post.isAnonymous ? '匿名委托无法投币' : '给作者投喂丁尼'"
                              @click="giveDenny"
                            >
                              <span class="ik-triple__icon-shell">
                                <img src="/images/materials/dennies_v2.webp" class="ik-engage-icon ik-engage-icon--denny" alt="投币" />
                                <TripleChargeRing :progress="tripleCharge.progress" :show="tripleCharge.active" />
                              </span>
                              <IkRollingDigit :value="post.dennyCount ?? 0" />
                            </button>
                            <button
                              type="button"
                              class="ik-engage-bar__action"
                              :class="{ 'ik-engage-bar__action--active': post.favorited }"
                              :disabled="favoriting"
                              @click="favoriteArticle"
                            >
                              <span class="ik-triple__icon-shell">
                                <StarIconSolid v-if="post.favorited" class="ik-engage-icon" aria-hidden="true" />
                                <StarIcon v-else class="ik-engage-icon" aria-hidden="true" />
                                <TripleChargeRing :progress="tripleCharge.progress" :show="tripleCharge.active" />
                              </span>
                              <IkRollingDigit :value="post.favoritesCount ?? 0" />
                            </button>
                            <z-dropdown
                              trigger="click"
                              size="small"
                              class="ik-engage-bar__more"
                              @command="handleArticleMenuCommand"
                            >
                              <button type="button" class="ik-engage-bar__action" title="更多操作">
                                <EllipsisVerticalIcon class="ik-engage-icon" aria-hidden="true" />
                              </button>
                              <template #dropdown>
                                <z-dropdown-item command="report" :disabled="isOwner">举报委托</z-dropdown-item>
                                <z-dropdown-item v-if="isAdmin" :command="post?.isPinned ? 'unpin' : 'pin'" :disabled="pinningArticle">{{ post?.isPinned ? '取消置顶' : '置顶' }}</z-dropdown-item>
                                <z-dropdown-item command="edit" :disabled="!isOwner">编辑委托</z-dropdown-item>
                                <z-dropdown-item command="delete" :disabled="!isOwner || deletingArticle">删除委托</z-dropdown-item>
                              </template>
                            </z-dropdown>
                          </div>
                        </div>
                      </div>

                      <div class="ik-engage-bar__bottom">
                        <div class="ik-engage-bar__bottom-inner">
                          <div class="ik-engage-bar__left-icons">
                      <button
                        type="button"
                        class="ik-engage-bar__tool"
                        :class="{ 'ik-engage-bar__tool--active': commentImages.uploadTasks.value.length > 0 }"
                        aria-label="添加图片"
                        :title="commentImages.remainingImageSlots.value <= 0 ? '图片数量已达上限' : '添加图片'"
                        :disabled="commentImages.remainingImageSlots.value <= 0"
                        @click.stop="openCommentImagePicker"
                      >
                        <PhotoIcon class="ik-engage-icon" aria-hidden="true" />
                      </button>
                            <!-- @ 按钮：在光标处插入 @ 并立即弹出 picker。
                                 click.stop 阻止冒泡到外层的 focusCommentInput。 -->
                            <button
                              type="button"
                              class="ik-engage-bar__tool"
                              aria-label="@"
                              :disabled="mention.isAtLimit.value"
                              :title="mention.isAtLimit.value ? '已达到提及上限' : '提及用户'"
                              @click.stop="mention.insertAtTrigger"
                            >
                              <AtSymbolIcon class="ik-engage-icon" aria-hidden="true" />
                            </button>
                            <!-- 表情按钮：切换底部表情面板，可连续插入 -->
                            <button
                              type="button"
                              class="ik-engage-bar__tool"
                              :class="{ 'ik-engage-bar__tool--active': emotePickerVisible }"
                              aria-label="表情"
                              :disabled="emoteInsert.isAtLimit.value"
                              :title="emoteInsert.isAtLimit.value ? '表情数量已达上限' : '插入表情'"
                              @click.stop="toggleEmotePicker"
                              @mousedown.stop
                            >
                              <FaceSmileIcon class="ik-engage-icon" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              class="ik-engage-bar__tool"
                              :class="{ 'ik-engage-bar__tool--active': commentAnonymous }"
                              :aria-label="commentAnonymous ? '取消匿名' : '匿名评论'"
                              :title="commentAnonymous ? '取消匿名' : '匿名评论'"
                              @click.stop="toggleAnonymous"
                            >
                              <EyeSlashIcon v-if="commentAnonymous" class="ik-engage-icon" aria-hidden="true" />
                              <EyeIcon v-else class="ik-engage-icon" aria-hidden="true" />
                            </button>
                          </div>
                          <div class="ik-engage-bar__right-btns">
                            <button
                              type="button"
                              class="ik-engage-bar__submit"
                              :disabled="!canSubmitComment"
                              @click="sendComment"
                            >
                              {{ sendingComment ? "发送中" : "发送" }}
                            </button>
                            <button type="button" class="ik-engage-bar__cancel" @click="cancelComment">
                              取消
                            </button>
                          </div>
                        </div>
                      </div>

                    </div>

                    <EmotePicker
                      v-if="!isMobile"
                      :visible="emotePickerVisible"
                      :anchor="emotePickerAnchor"
                      @select="onEmoteSelect"
                      @select-emoji="onEmojiSelect"
                      @close="emotePickerVisible = false"
                    />
                    <MobileEmotePicker
                      v-else
                      :visible="emotePickerVisible"
                      @select="onEmoteSelect"
                      @select-emoji="onEmojiSelect"
                      @close="emotePickerVisible = false"
                    />
                  </div>
                </div>
              </div>
            </template>
            </div><!-- /.ik-dialog__main -->
          </div>
        </div>
      </div>
      </FocusScope>

      <!-- @ 提及候选下拉：组件内部 Teleport 到 body，避免父级 overflow 截断；
           放在 .ik-overlay 内只是为了保持单根模板，不影响渲染位置。 -->
      <MentionPicker
        :visible="mention.pickerVisible.value"
        :loading="mention.pickerLoading.value"
        :results="mention.pickerResults.value"
        :active-index="mention.pickerActiveIndex.value"
        :anchor="mention.pickerAnchor.value"
        @select="mention.selectCandidate"
        @hover="(idx: number) => (mention.pickerActiveIndex.value = idx)"
      />

      <Teleport to="body">
        <Transition name="ik-overlay" appear>
          <PostImagePickerModal
            v-if="commentImages.showImagePickerModal.value"
            :existing-ids="commentImages.existingUploadIds.value"
            :remaining="commentImages.remainingImageSlots.value"
            @close="commentImages.showImagePickerModal.value = false"
            @upload="commentImages.handleImagePickerUpload"
            @select="commentImages.handleImagePickerSelect"
            @delete="commentImages.removeUploadByServerId($event.documentId)"
          />
        </Transition>
      </Teleport>
    </div>
</template>

<style scoped>
/* ═══════════════════════════════════════════════
   ZZZ-Style Post Overlay
   Matches Flutter: showZZZDialog + PostPage
   ═══════════════════════════════════════════════ */

/* ── Backdrop ──────────────────────────────────── */
.ik-overlay.ik-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  --ik-overlay-bg: rgba(0, 0, 0, 0.6);
}

/* 独立背景层：承载遮罩颜色，用 opacity 过渡替代 .ik-overlay 的 background-color 过渡。
   移动端/桌面端颜色通过 --ik-overlay-bg 变量切换，入场时只让 opacity 变化，避免全屏 paint。
   毛玻璃也挂在这一层：元素 opacity 同样作用于 backdrop-filter 的结果，模糊会随遮罩
   一起淡入淡出；挂在根节点上时模糊不参与过渡，第一帧就直接跳满。 */
.ik-overlay__backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: var(--ik-overlay-bg);
  opacity: 1;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

/* 斜线纹理（PatternPainter）—— web 端调优版：
   40°、~9px 间距、3px 线宽、白 alpha 0.09。
   不是严格复刻 Flutter（45°/5px/1.5px/0.15），而是过去针对 web 端
   显示密度与对比度调过的更舒适版本，保留。 */
.ik-overlay__stripe {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: repeating-linear-gradient(
    40deg,
    transparent,
    transparent 3.5px,
    rgba(255, 255, 255, 0.09) 4.5px,
    rgba(255, 255, 255, 0.09) 7.5px,
    transparent 8.5px
  );
  /* 与入场时全局规则给的 will-change 保持一致：入场结束摘掉 will-change 会让这层
     全屏斜纹降级回父层，触发一次全屏重绘，正好落在动画停下的那一帧。 */
  will-change: opacity;
}

/* ── Dialog Shell ──────────────────────────────── */
.ik-dialog {
  position: relative;
  width: 70%;
  height: 70%;
  /* desktop 缩放 1.1x → 实际占 77% */
  transform: scale(1.1);
  transform-origin: center;
  /* 同上：常驻合成层，避免入场结束时降级并整体重新光栅化 */
  will-change: transform, opacity;
}

/* 无 GPU 时入场动画已关闭，常驻合成层只会增加软件合成开销 */
html.no-gpu .ik-dialog {
  will-change: auto;
}

/* 外边框 */
.ik-dialog__outer {
  width: 100%;
  height: 100%;
  padding: 4px;
  background: #2D2C2D;
  border-radius: 24px 0 24px 24px;
  overflow: hidden;
}

/* 内边框 */
.ik-dialog__inner {
  width: 100%;
  height: 100%;
  padding: 4px;
  background: #000;
  border-radius: 22px 0 22px 22px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Header Bar ────────────────────────────────── */
.ik-dialog__header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 18px 0 0 0;
  background:
    url("/images/tab-bg-point.webp") repeat,
    linear-gradient(180deg, #161616 0%, #080808 100%);
}

/* ── Content shell（header 下方，承载跑马灯 + body） ─ */
.ik-dialog__main {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ik-dialog__gallery-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 3px;
  overflow: hidden;
  pointer-events: none;
  opacity: 0;
  z-index: 1;
  transition: opacity 0.3s ease;
}

.ik-dialog__gallery-progress.is-active {
  opacity: 1;
}

.ik-dialog__gallery-progress-bar {
  height: 100%;
  background: #fbfe00;
  box-shadow: 0 0 8px rgba(251, 254, 0, 0.6);
  transition: width 0.2s ease;
}

.ik-dialog__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.ik-dialog__avatar-shell {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 3px solid #2d2d2d;
  overflow: hidden;
}

.ik-dialog__avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 999px;
  background: #1b1b1b;
}

.ik-dialog__avatar--placeholder {
  background: #2a2a2a;
}

/* 头像、关闭按钮和 blur-up 占位图跳过全局 img 淡入：全局规则让新建的 <img> 一直透明到
   load 事件触发，即使已有缓存也会在入场时一个个「冒出来」。 */
.ik-dialog__avatar,
.ik-dialog__close-img,
.ik-dialog__cover-preview-image {
  opacity: 1;
  transition: none;
}

.ik-dialog__author-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.ik-dialog__author-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ik-dialog__author-name {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ik-dialog__avatar-wrap {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}

.ik-dialog__level {
  position: absolute;
  top: -4px;
  left: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: #000;
  border: 2px solid #000;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.ik-dialog__popularity {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 18px;
  padding: 0 6px 0 4px;
  border-radius: 6px;
  background: #2a2a2a;
  color: #fff;
  font-size: 12px;
  font-weight: 900;
  line-height: 1;
}

.ik-dialog__popularity-icon {
  width: 12px;
  height: 12px;
}

.ik-dialog__time {
  margin: 16px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: #808080;
}

/* ── 「⋮」上拉菜单：z-dropdown 默认向下展开，这里改为向上 ── */
.ik-engage-bar__more {
  margin-left: 0;
  z-index: 5;
}

.ik-engage-bar__more :deep(.z-dropdown__content) {
  bottom: calc(100% + 8px);
  top: auto;
  transform-origin: bottom;
  transform: scaleY(0.6);
  transition: opacity 0.18s ease,
              transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.ik-engage-bar__more.is-visible :deep(.z-dropdown__content) {
  transform: scaleY(1);
}

.ik-engage-bar__more :deep(.z-dropdown__content)::before {
  top: 0;
  bottom: -8px;
}

.ik-dialog__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 140ms ease, transform 140ms ease;
}

.ik-dialog__close:hover {
  opacity: 0.85;
  transform: scale(1.08);
}

.ik-dialog__close:active {
  transform: scale(0.95);
}

.ik-dialog__close-img {
  height: 32px;
  width: auto;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

/* ── Skeleton ──────────────────────────────────── */
@keyframes ik-shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

.ik-skel {
  border-radius: 6px;
  background: linear-gradient(90deg, #1a1a1a 25%, #252525 50%, #1a1a1a 75%);
  background-size: 800px 100%;
  animation: ik-shimmer 1.6s ease infinite;
}

/* background-position 动画无法交给合成器，每帧都要重绘弹窗主体图层；
   入场 / 离场 / 切换动画期间先停住，让 transform/opacity 动画只走合成器。 */
.ik-overlay-enter-active .ik-skel,
.ik-overlay-leave-active .ik-skel,
.ik-dialog--switching .ik-skel {
  animation-play-state: paused;
}

.ik-skel--cover {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
}

.ik-skel--title {
  width: 55%;
  height: 26px;
  margin-bottom: 16px;
  border-radius: 4px;
}

.ik-skel--line {
  height: 14px;
  margin-top: 8px;
  border-radius: 3px;
}

.ik-skel--actions-bar {
  width: 100%;
  height: 36px;
  border-radius: 18px;
}

/* ── Error ─────────────────────────────────────── */
.ik-dialog__error {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #ffb1b1;
  background: #121212;
  text-align: center;
}

/* ── Body (两栏 / 单栏) ───────────────────────── */
.ik-dialog__body {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  min-height: 0;
  background: transparent;
  border-radius: 0 0 18px 18px;
}

/* ── Left Column ───────────────────────────────── */
.ik-dialog__left {
  flex: 3;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.ik-dialog__left-scroll {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
  margin: 16px 8px 16px 16px;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 16px;
  /* 隐藏滚动条 */
  -ms-overflow-style: none;
  scrollbar-width: none;
  /* GPU 硬件加速层提升：使该滚动区域在独立的合成层中渲染，避免与父级 backdrop-filter 冲突导致滚动重绘掉帧 */
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
}

.ik-dialog__left-scroll::-webkit-scrollbar {
  display: none;
}

/* 封面：桌面端是左侧一张撑满高度的竖卡，图片在卡内居中、上下留黑 */
.ik-dialog__cover-wrap {
  flex: 0 0 52%;
  min-width: 0;
  display: flex;
  padding: 16px;
}

.ik-dialog__cover-border {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 16px;
  border: 4px solid #3d3d3d;
  box-shadow: 0 0 0 3px #111;
  overflow: hidden;
  background: #0a0a0a;
}

.ik-dialog__cover-skel {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 1;
}

/* 轮播轨道是一整个合成层，屏外 slide 的 shimmer（background-position 动画）
   也会让整条轨道每帧重绘、重新光栅化里面的大图，切图时直接掉帧。 */
.ik-dialog__cover-skel--offscreen {
  animation-play-state: paused;
}

.ik-dialog__cover-preview {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.ik-dialog__cover-preview-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(16px);
  transform: scale(1.05);
}

:deep(.ik-dialog__cover) {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: var(--ik-cursor-pointer);
}

/* 多图轮播 */
.ik-dialog__cover-scroller {
  position: absolute;
  inset: 0;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
}

.ik-dialog__cover-track {
  display: flex;
  height: 100%;
  touch-action: pan-y pinch-zoom;
}

.ik-dialog__cover-slide {
  position: relative;
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  min-width: 0;
}

/* 正文：封面右侧单独滚动的一列 */
.ik-dialog__detail-frame {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
}

.ik-dialog__detail {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 20px 32px 4px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.ik-dialog__detail::-webkit-scrollbar {
  display: none;
}

.ik-dialog__hidden-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border: 1px solid rgba(255, 170, 0, 0.4);
  border-radius: 0 10px 10px 10px;
  background: rgba(255, 170, 0, 0.08);
  color: #ffaa00;
  font-size: 13px;
  line-height: 1.5;
}

.ik-dialog__hidden-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.ik-dialog__solved {
  display: inline-block;
  margin-right: 8px;
  padding: 1px 8px;
  border-radius: 5px;
  background: #14351f;
  color: #4ade80;
  font-size: 0.6em;
  font-weight: 700;
  vertical-align: middle;
}

.ik-dialog__title-cat {
  margin-right: 6px;
}

.ik-dialog__title {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.5;
  letter-spacing: 0.5px;
  color: #fff;
}

.ik-dialog__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 16px 0 0;
}

.ik-dialog__tag {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: #4a9eff;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.6;
  cursor: pointer;
  transition: color 0.15s ease;
}

.ik-dialog__tag:hover {
  color: #7ab8ff;
  text-decoration: underline;
}

.ik-dialog__content {
  margin: 0;
  font-size: 16px;
  line-height: 1.75;
  color: #e0e0e0;
  /* 由 markdown-it 渲染成 HTML，无需 pre-wrap；详见 utils/format-body.ts。 */
  white-space: normal;
  word-wrap: break-word;
}

/* markdown-it 在"段落内嵌块级 HTML"场景会产出空 <p></p>，过滤掉避免多余间距。 */
.ik-dialog__content :deep(p:empty) {
  display: none;
}

.ik-dialog__content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  cursor: pointer;
}

.ik-dialog__content :deep(a) {
  color: #6f9cff;
  text-decoration: underline;
}

.ik-dialog__content :deep(pre) {
  background: #1a1a1a;
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
}

.ik-dialog__content :deep(code) {
  background: #1a1a1a;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', monospace;
}

.ik-dialog__content :deep(blockquote) {
  border-left: 4px solid #BFFF09;
  padding-left: 16px;
  margin: 12px 0;
  color: #b0b0b0;
}

.ik-dialog__content :deep(table) {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 14px;
  background: #111;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
}
.ik-dialog__content :deep(table th),
.ik-dialog__content :deep(table td) {
  padding: 8px 12px;
  border: 1px solid #2a2a2a;
  text-align: left;
  vertical-align: top;
}
.ik-dialog__content :deep(table thead th) {
  background: #1a1a1a;
  color: #fff;
  font-weight: 700;
}
.ik-dialog__content :deep(table tbody tr:nth-child(even)) {
  background: #161616;
}

.ik-dialog__videos {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

/* ── Right Column ──────────────────────────────── */
.ik-dialog__right {
  position: relative;
  flex: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  margin: 16px 16px 16px 8px;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 16px;
  overflow: hidden;
}

.ik-dialog__comments-frame {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ik-dialog__comments-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  /* GPU 硬件加速层提升：避免列表滚动时引起父层级的重绘，保障评论滑动流畅 */
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
}

.ik-dialog__comments-scroll::-webkit-scrollbar {
  display: none;
}

.ik-dialog__comments-inner {
  width: 100%;
  min-height: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.ik-dialog__comments-inner {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ik-dialog__comments-toolbar-spacer {
  height: 53px;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.ik-dialog__comments-heading {
  /* 桌面端照游戏不显示标题，移动端再打开 */
  display: none;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 12px;
  color: #ededed;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 700;
}

.ik-dialog__comments-heading span {
  color: #9a9a9a;
  font-size: 13px;
  font-weight: 400;
}

.ik-comments-sentinel {
  width: 100%;
  min-height: 1px;
}

.ik-comments-earlier {
  display: block;
  width: 100%;
  padding: 10px 0;
  border: none;
  border-top: 3px solid #1e1e1e;
  background: transparent;
  color: #888;
  font-size: 13px;
  font-weight: 600;
  cursor: var(--ik-cursor-pointer);
  transition: color 140ms ease;
}

.ik-comments-earlier:hover:not(:disabled) {
  color: var(--ik-primary);
}

.ik-scroll-footer {
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 0;
}

.ik-comments-end {
  color: #5a5a5a;
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 1px;
}

.ik-scroll-gif {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

/* ── Actions Bar ───────────────────────────────── */
.ik-dialog__actions {
  position: relative;
  z-index: 4;
  flex-shrink: 0;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: transparent;
  border-top: 3px solid #2a2a2a;
}

.ik-dialog__input-bar {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.ik-dialog__comment-input {
  flex: 1;
}

.ik-dialog__send-btn {
  flex-shrink: 0;
}

.ik-engage-bar {
  display: flex;
  flex-direction: column;
  color: #f5f5f5;
}



.ik-engage-bar__main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  /* 不能 overflow:hidden：会把「⋮」上拉菜单剪掉 */
  overflow: visible;
}

.ik-engage-bar__content-edit {
  position: relative;
  flex: 1 1 0%;
  min-width: 0;
  min-height: 44px;
  cursor: text;
  transition: border-radius 160ms ease;
}

.ik-engage-bar__textarea {
  width: 100%;
}

.ik-engage-bar__textarea :deep(.z-input),
.ik-engage-bar__textarea :deep(.z-textarea) {
  min-height: 44px;
  max-height: 64px;
  border: 3px solid #3a3a3a !important;
  border-radius: 999px !important;
  box-shadow: none !important;
  background: #1a1a1a !important;
  overflow: hidden;
  transition: border-color 160ms ease,
              background-color 160ms ease,
              border-radius 160ms ease;
}

.ik-engage-bar__textarea :deep(.z-input::after),
.ik-engage-bar__textarea :deep(.z-textarea::after) {
  display: none !important;
}

.ik-dialog__actions--active .ik-engage-bar__textarea :deep(.z-input),
.ik-dialog__actions--active .ik-engage-bar__textarea :deep(.z-textarea) {
  border-color: #4a4a4a !important;
  border-radius: 20px !important;
  background: #111 !important;
}

.ik-engage-bar__textarea :deep(.z-input:hover),
.ik-engage-bar__textarea :deep(.z-input.is-focused),
.ik-engage-bar__textarea :deep(.z-textarea:hover),
.ik-engage-bar__textarea :deep(.z-textarea.is-focused) {
  box-shadow: none !important;
}

.ik-engage-bar__textarea :deep(.z-input__inner),
.ik-engage-bar__textarea :deep(.z-textarea__inner),
.ik-engage-bar__textarea :deep(textarea) {
  height: 42px;
  min-height: 42px;
  max-height: 62px;
  padding: 11px 16px !important;
  border: none;
  outline: none;
  box-shadow: none;
  resize: none !important;
  overflow-y: auto;
  background: transparent !important;
  color: #f5f5f5 !important;
  font: inherit;
  font-size: 14px !important;
  line-height: 20px !important;
  box-sizing: border-box;
}

.ik-engage-bar__textarea :deep(.z-input__inner::placeholder),
.ik-engage-bar__textarea :deep(.z-textarea__inner::placeholder),
.ik-engage-bar__textarea :deep(textarea::placeholder) {
  color: transparent;
}

.ik-engage-bar__reply-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 0;
  font-size: 12px;
  color: #BFFF09;
}

.ik-engage-bar__reply-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: #999;
  font-size: 11px;
  cursor: pointer;
  border-radius: 999px;
  transition: color 140ms ease, background-color 140ms ease;
}

.ik-engage-bar__reply-close:hover {
  color: #fff;
  background: #333;
}

.ik-engage-bar__placeholder {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  color: #8b8b8b;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
}

.ik-engage-bar__placeholder-avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  object-fit: cover;
  background: #2a2a2a;
}

.ik-engage-bar__attachments {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 8px;
  margin: 10px 0 0;
}

.ik-engage-bar__attachment {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.ik-engage-bar__attachment-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ik-engage-bar__attachment-progress,
.ik-engage-bar__attachment-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  text-align: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.48);
  font-size: 12px;
  font-weight: 700;
}

.ik-engage-bar__attachment-error {
  border: 0;
  line-height: 1.3;
  cursor: var(--ik-cursor-pointer);
}

.ik-engage-bar__attachment-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  line-height: 1;
  cursor: var(--ik-cursor-pointer);
}

.ik-engage-bar__attachment-remove:hover {
  background: rgba(0, 0, 0, 0.85);
}

.ik-engage-bar__interact-container {
  flex: none;
  width: auto;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: translateX(0);
  will-change: width, transform, opacity;
  transition: width 220ms cubic-bezier(0.22, 1, 0.36, 1),
              opacity 140ms ease,
              transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.ik-dialog__actions--active .ik-engage-bar__interact-container {
  width: 0;
  min-width: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateX(36px);
  pointer-events: none;
}

.ik-engage-bar__buttons {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ik-engage-bar__action,
.ik-engage-bar__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #f1f1f1;
  cursor: pointer;
  transition: color 140ms ease, transform 140ms ease;
}

.ik-engage-bar__action {
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.ik-triple__icon-shell {
  position: relative;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
}

.ik-engage-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.ik-engage-icon--denny {
  width: 24px;
  height: 24px;
  margin: 0;
  object-fit: contain;
  opacity: 0.65;
  transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.ik-engage-bar__action:hover:not(:disabled) .ik-engage-icon--denny {
  transform: scale(1.1) rotate(15deg);
  opacity: 0.9;
}

.ik-engage-bar__action--active .ik-engage-icon--denny {
  opacity: 1 !important;
}

.ik-engage-bar__action:disabled .ik-engage-icon--denny {
  opacity: 0.3;
}

.ik-engage-bar__action:hover,
.ik-engage-bar__tool:hover {
  color: var(--ik-primary);
}

.ik-engage-bar__action--active {
  color: var(--ik-primary);
}

.ik-engage-bar__action:active,
.ik-engage-bar__tool:active {
  transform: scale(0.94);
}

.ik-engage-bar__bottom {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-4px);
  will-change: max-height, transform, opacity;
  transition: max-height 180ms ease, opacity 140ms ease, transform 180ms ease;
}

.ik-dialog__actions--active .ik-engage-bar__bottom {
  max-height: 44px;
  margin-top: 8px;
  opacity: 1;
  transform: translateY(0);
}

.ik-engage-bar__bottom-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
}

.ik-engage-bar__left-icons,
.ik-engage-bar__right-btns {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ik-engage-bar__tool {
  width: 28px;
  height: 28px;
  color: #bfbfbf;
}

.ik-engage-bar__tool--active {
  color: var(--ik-primary);
  background: rgba(191, 255, 9, 0.12);
  border-radius: 6px;
}

.ik-engage-bar__submit,
.ik-engage-bar__cancel {
  height: 30px;
  padding: 0 16px;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  transition: opacity 140ms ease, transform 140ms ease, background-color 140ms ease;
}

.ik-engage-bar__submit {
  background: var(--ik-primary);
  color: #000;
}

.ik-engage-bar__submit:disabled {
  background: #4a4a4a;
  color: #9a9a9a;
  cursor: not-allowed;
}

.ik-engage-bar__cancel {
  background: transparent;
  color: #bfbfbf;
}

.ik-engage-bar__submit:not(:disabled):hover,
.ik-engage-bar__cancel:hover {
  opacity: 0.86;
}

.ik-engage-bar__submit:not(:disabled):active,
.ik-engage-bar__cancel:active {
  transform: scale(0.96);
}

/* ═══════════════════════════════════════════════
   Transition Animations —— 仅覆盖 transform 起止值
   ───────────────────────────────────────────────
   动画时长 / 曲线 / will-change 等在全局 theme.css 里定义（所有
   .ik-overlay 弹窗共用）。此处仅因为本弹窗 .ik-dialog 自带
   transform: scale(1.1) 静态变换，需在 enter-from / leave-to
   补回 scale(1.1) 部分，避免动画过程中弹窗被缩到 1.0。
   ═══════════════════════════════════════════════ */

.ik-overlay-enter-from .ik-dialog {
  /* FractionalTranslation(0.05, 0)：CSS translateX(5%) 也是元素自身宽度的 5%，等价 */
  transform: scale(1.1) translateX(5%);
}

.ik-overlay-leave-to .ik-dialog {
  transform: scale(1.1) translateX(-5%);
}

/* 浮层内切换委托（通知等跳转）时，仅让弹窗主体 .ik-dialog 重放一次
   与瀑布流点开一致的滑入+淡入；背景遮罩、斜纹保持不动，观感为「新委托进场」
   而非「原地替换内容」。桌面端 base transform 为 scale(1.1)。 */
@keyframes ik-dialog-switch-in {
  from { opacity: 0; transform: scale(1.1) translateX(5%); }
  to   { opacity: 1; transform: scale(1.1) translateX(0); }
}

.ik-dialog--switching {
  animation: ik-dialog-switch-in 200ms cubic-bezier(0.165, 0.84, 0.44, 1);
}

@media (prefers-reduced-motion: reduce) {
  .ik-dialog--switching {
    animation: none;
  }
}

/* 保留根元素 transition 0.2s，让 Vue 的 <Transition> 能正确计算离场时长。
   实际用 opacity 作为无动画占位，真正的退场仍由 .ik-overlay__backdrop 的 opacity
   与 .ik-dialog 的 transform/opacity 完成。 */
.ik-overlay.ik-overlay-enter-active,
.ik-overlay.ik-overlay-leave-active {
  transition: opacity 0.2s;
  will-change: auto;
}

.ik-overlay.ik-overlay-enter-active .ik-overlay__backdrop {
  transition: opacity 200ms var(--ease-out-quart);
  will-change: opacity;
}

.ik-overlay.ik-overlay-enter-from .ik-overlay__backdrop {
  opacity: 0;
}

.ik-overlay.ik-overlay-leave-active .ik-overlay__backdrop {
  transition: opacity 200ms var(--ease-in-quart);
  will-change: opacity;
}

.ik-overlay.ik-overlay-leave-to .ik-overlay__backdrop {
  opacity: 0;
}

/* ═══════════════════════════════════════════════
   Mobile & Tablet Overlay (< 1024px)
   ═══════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .ik-overlay.ik-overlay {
    --ik-overlay-bg: rgba(0, 0, 0, 0.88);
  }

  .ik-overlay__backdrop {
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
}

/* ═══════════════════════════════════════════════
   Mobile & Tablet Layout (< 1024px)
   ═══════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .ik-dialog {
    width: 90%;
    height: 90%;
    transform: scale(1);
  }

  .ik-overlay-enter-from .ik-dialog {
    transform: scale(1) translateX(5%);
  }

  .ik-overlay-leave-to .ik-dialog {
    transform: scale(1) translateX(-5%);
  }

  /* 移动/平板端 base transform 为 scale(1)，切换委托的重放动画同步用 scale(1) 版关键帧 */
  @keyframes ik-dialog-switch-in-mobile {
    from { opacity: 0; transform: scale(1) translateX(5%); }
    to   { opacity: 1; transform: scale(1) translateX(0); }
  }

  .ik-dialog--switching {
    animation-name: ik-dialog-switch-in-mobile;
  }

  /* 移动端整体变为一条整页滚动的单栏：封面 + 正文 + 评论一起滚 */
  .ik-dialog__body {
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    -ms-overflow-style: none;
    scrollbar-width: none;
    border-radius: 0;
    /* 保持透明，让 .ik-dialog__main 里的跑马灯水印透出来（与桌面端一致） */
    background: transparent;
    /* 入场动画期间避免强制提升为独立合成层，减少 GPU 层数；
       实际滚动时浏览器会按需提升，iOS 触摸滚动不会卡死 */
    will-change: auto;
    -webkit-transform: none;
  }

  .ik-dialog__body--scrollable {
    will-change: scroll-position;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }

  .ik-dialog__body::-webkit-scrollbar {
    display: none;
  }

  .ik-dialog__left {
    flex: none;
  }

  /* 左栏不再独立滚动，内容自然撑开成为整页滚动的一部分 */
  .ik-dialog__left-scroll {
    flex: none;
    max-height: none;
    overflow: visible;
    margin: 0;
    border-radius: 0;
    will-change: auto;
    transform: none;
  }

  .ik-dialog__left-scroll {
    display: block;
  }

  .ik-dialog__cover-wrap {
    display: block;
    padding: 0;
  }

  .ik-dialog__cover-border {
    height: auto;
    max-height: 50vh;
    /* 移动端按图片比例撑开（模板里按封面写入 --ik-cover-ratio） */
    aspect-ratio: var(--ik-cover-ratio, auto);
    display: block;
    border-radius: 0;
    border-width: 0 0 4px;
    box-shadow: none;
  }

  .ik-dialog__detail-frame,
  .ik-dialog__comments-frame {
    display: contents;
  }

  /* Embla 已经通过 touch-action 处理好横向拖拽与纵向翻页的协调，
     这里显式保留纵向滚动穿透，避免和整体弹窗滚动冲突。 */
  .ik-dialog__cover-scroller {
    touch-action: pan-y pinch-zoom;
  }

  .ik-dialog__detail {
    overflow: visible;
    padding: 16px;
  }

  /* 右栏不再独立滚动；overflow 必须 visible 才能让底部互动栏 sticky 生效 */
  .ik-dialog__right {
    flex: 1 0 auto;
    margin: 0;
    border-radius: 0;
    overflow: visible;
    border-top: 1px solid #313132;
    background: transparent;
  }

  .ik-dialog__comments-scroll {
    flex: 1 0 auto;
    min-height: 0;
    overflow: visible;
    will-change: auto;
    transform: none;
  }

  .ik-dialog__comments-heading {
    display: flex;
  }

  /* 底部互动栏固定在屏幕底部，不随内容滚走 */
  .ik-dialog__actions {
    position: sticky;
    bottom: 0;
    z-index: 3;
    background: #0a0a0a;
    border-top: 1px solid #202020;
  }

  .ik-dialog__actions--emote-open {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    bottom: var(--vv-bottom, 0px);
    z-index: 100;
  }

  .ik-dialog__body--emote-open {
    padding-bottom: calc(var(--emote-panel-height) + 120px + env(safe-area-inset-bottom));
  }
}

@media (max-width: 500px) {
  /* 手机端：真·全屏，去掉弹窗边距与圆角，消除"放大弹窗"的观感 */
  .ik-dialog {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
  }

  /* will-change: transform 同样会成为 fixed 子元素的包含块，必须一起去掉，
     否则表情面板打开时底部互动栏的 position: fixed 会失效 */
  .ik-dialog--emote-open {
    transform: none;
    will-change: auto;
  }

  .ik-dialog--emote-open .ik-dialog__body--scrollable {
    transform: none;
    -webkit-transform: none;
    will-change: auto;
  }

  .ik-dialog__outer {
    border-radius: 0;
  }

  .ik-dialog__inner {
    border-radius: 0;
  }

  .ik-dialog--emote-open .ik-dialog__outer,
  .ik-dialog--emote-open .ik-dialog__inner {
    overflow: visible;
  }

  /* 顶部 header 适配刘海/状态栏安全区 */
  .ik-dialog__header {
    padding-top: calc(8px + env(safe-area-inset-top));
    border-radius: 0;
  }

  /* 底部互动栏适配 Home 指示条安全区 */
  .ik-dialog__actions {
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
  }
}

</style>
